import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { ctaToKakao } from "../utils/cloneCta";
import SEO from "../components/SEO";
import { BRAND } from "../config/brand";
import { PdfPreviewModal, BrandHeader, BrandFooter, PageQR } from "../components/PrintPdfShell";
import ProductPageEditor from "../components/ProductPageEditor";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import InlineInquiryForm from "../components/InlineInquiryForm";
import ReviewSection from "../components/ReviewSection";
import NaverPayButton from "../components/NaverPayButton";

/* ═══════════════════════════════════════════════════════════
   Apple-style 상세페이지 — Gemini AI 생성 콘텐츠 렌더러
   with scroll-triggered animations & polish
   ═══════════════════════════════════════════════════════════ */

/* ── 결합형 옵션 자동 분리: "흑백 2,000매 / 컬러 200매" → 흑백 + 컬러 별도 그룹 ── */
function splitCombinedOptionGroups(groups) {
  if (!groups || groups.length === 0) return groups;
  const comboPat = /흑백\s*([0-9,]+)\s*매.*?(?:칼라|컬러|color)\s*([0-9,]+)\s*매/i;
  const result = [];
  for (const g of groups) {
    const opts = g.options || [];
    const matches = opts.map(o => comboPat.exec(o?.name || String(o || "")));
    if (matches.length >= 2 && matches.every(Boolean)) {
      const bwOpts = [], clOpts = [];
      opts.forEach((opt, i) => {
        const m = matches[i];
        const up = Number(opt?.unit_price) || 0;
        const bwQty = Number(m[1].replace(/,/g, "")) || 0;
        bwOpts.push({ ...opt, name: `${m[1]}매`, unit_price: 10, price_label: `합계 ${(10 * bwQty).toLocaleString()}원` });
        const clQty = Number(m[2].replace(/,/g, "")) || 0;
        clOpts.push({ ...opt, name: `${m[2]}매`, unit_price: up || 100, price_label: `합계 ${((up || 100) * clQty).toLocaleString()}원` });
      });
      result.push({ ...g, group_name: "흑백 출력매수", group_description: "월 흑백 기본 출력 매수를 선택하세요.", options: bwOpts });
      result.push({ ...g, group_name: "컬러 출력매수", group_description: "월 컬러 기본 출력 매수를 선택하세요.", options: clOpts });
    } else {
      result.push(g);
    }
  }
  return result;
}

const isOutputCountGroup = (group) => {
  const text = `${group?.group_name || ""} ${group?.group_description || ""}`;
  if (!/(출력\s*매수|출력매수|흑백|칼라|컬러|매수)/i.test(text)) return false;
  return (group?.options || []).some(opt => /[0-9][0-9,\s]*\s*매/.test(String(opt?.name || "")));
};

const getOptionGroupPriceType = (group) => {
  if (group?.price_type) return group.price_type;
  return isOutputCountGroup(group) ? "per_unit" : "fixed";
};

const isPerUnitPriceGroup = (group) => getOptionGroupPriceType(group) === "per_unit";

const getOptionQuantityFromName = (name) => {
  const match = String(name || "").replace(/[,\s]/g, "").match(/(\d+)/);
  return match ? Number(match[1]) : 1;
};

const getOptionPriceAmount = (group, opt) => {
  const unitPrice = Number(opt?.unit_price);
  if (!(unitPrice > 0)) return 0;
  return isPerUnitPriceGroup(group) ? unitPrice * getOptionQuantityFromName(opt?.name) : unitPrice;
};

const normalizeOrderQuantity = (value) => {
  const digits = String(value ?? "").replace(/[^\d]/g, "");
  const parsed = Number.parseInt(digits, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

function QuantityInput({ value, onChange, compact = false, variant = "light" }) {
  const isDark = variant === "dark";
  const current = normalizeOrderQuantity(value);
  const setQty = (n) => onChange(normalizeOrderQuantity(n));
  const handleChange = (e) => onChange(normalizeOrderQuantity(e.target.value));
  const handleBlur = () => onChange(current);
  const atMin = current <= 1;
  const stepClass = `${compact ? "w-7 h-7" : "w-8 h-8"} shrink-0 rounded-full inline-flex items-center justify-center transition`;
  const stepStyle = (disabled) => ({
    background: isDark ? "rgba(255,255,255,0.16)" : "var(--ink-25)",
    border: isDark ? "1.5px solid rgba(255,255,255,0.3)" : "1.5px solid var(--ink-150)",
    color: isDark ? "white" : "var(--ink-800)",
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  });
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full ${compact ? "px-2 py-1" : "px-3 py-2"}`}
      style={{
        background: isDark ? "rgba(255,255,255,0.12)" : "white",
        border: isDark ? "1.5px solid rgba(255,255,255,0.28)" : "1.5px solid var(--ink-150)",
        color: isDark ? "white" : "var(--ink-700)",
      }}
    >
      <span className={`${compact ? "hidden sm:inline" : ""} text-[12px] font-semibold mr-0.5`}>수량</span>
      <button
        type="button"
        onClick={() => setQty(current - 1)}
        disabled={atMin}
        aria-label="수량 1 감소"
        className={stepClass}
        style={stepStyle(atMin)}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.6 6h6.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={current}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-label="수량"
        className={`${compact ? "w-10 h-8" : "w-14 h-9"} rounded-full text-center text-sm font-bold outline-none`}
        style={{
          background: isDark ? "rgba(255,255,255,0.18)" : "var(--ink-25)",
          color: isDark ? "white" : "var(--ink-900)",
        }}
      />
      <button
        type="button"
        onClick={() => setQty(current + 1)}
        aria-label="수량 1 증가"
        className={stepClass}
        style={stepStyle(false)}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 2.6v6.8M2.6 6h6.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      <span className={`${compact ? "hidden sm:inline" : ""} text-[12px] font-semibold ml-0.5`}>개</span>
    </div>
  );
}

/* ── Scroll-triggered reveal hook ── */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); ob.unobserve(el); } },
      { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? "0px 0px -40px 0px" },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [options.threshold, options.rootMargin]);
  return [ref, visible];
}

/* ── Stagger helper: returns individual delay strings ── */
const stagger = (i, base = 0.08) => ({ transitionDelay: `${i * base}s` });

/* ── Counting animation hook ── */
function useCountUp(target, visible, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!visible || !target) return;
    const num = parseFloat(String(target).replace(/[^0-9.]/g, ""));
    if (isNaN(num) || num === 0) { setValue(target); return; }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(num * ease));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, target, duration]);
  const suffix = typeof target === "string" ? target.replace(/[0-9.,\s]/g, "") : "";
  return typeof target === "string" && isNaN(parseFloat(target.replace(/[^0-9.]/g, "")))
    ? target
    : `${value.toLocaleString()}${suffix}`;
}

/* ── SVG Icon Map — replaces all emoji with clean inline SVGs ── */
const SVG_ICONS = {
  precision: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14a4 4 0 110-8 4 4 0 010 8zm0-6a2 2 0 100 4 2 2 0 000-4z" />
  ),
  speed: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  shield: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5zm-1 15l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z" />
  ),
  palette: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10a2.5 2.5 0 002.5-2.5c0-.61-.23-1.21-.64-1.67A1.36 1.36 0 0113.5 17a2.5 2.5 0 002.5-2.5H20c0-5.51-4.49-10-8-10zM6.5 13a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
  ),
  lightning: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  leaf: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 21s1-4 5-4c3.5 0 5.5-2.5 6.5-5 .7-1.7 1-3.5 1-5.5 0-2-.5-3.5-.5-3.5S15 2 12 2C9 2 6 4 5 7c-1 3 0 6 1 8l-1 1m0 0l5-5" />
  ),
  gear: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
  ),
  cube: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
  ),
  layers: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
  ),
  zap: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  ),
  target: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M12 18a6 6 0 100-12 6 6 0 000 12z M12 14a2 2 0 100-4 2 2 0 000 4z" />
  ),
  award: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15a7 7 0 100-14 7 7 0 000 14z M8.21 13.89L7 23l5-3 5 3-1.21-9.12" />
  ),
  infinity: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
  ),
  cpu: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 7h10v10H7V7z M10 10h4v4h-4v-4z" />
  ),
  printer: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z" />
  ),
  toner: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 2h8v4H8V2zM6 6h12v4l-1 12H7L6 10V6z M9 10v6m3-6v6m3-6v6" />
  ),
  document: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" />
  ),
  chart: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 20V10M12 20V4M6 20v-6" />
  ),
  refresh: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M1 4v6h6M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
  ),
  lock: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2zm12 0V7a5 5 0 10-10 0v4" />
  ),
  wifi: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h.01M8.53 16.11a6 6 0 016.95 0M5.06 12.58a10 10 0 0113.87 0M1.5 9a14 14 0 0121 0" />
  ),
  cloud: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
  ),
  check: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
  ),
  star: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  ),
  heart: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
  ),
};

function SvgIcon({ name, size = 48, className = "" }) {
  const iconPath = SVG_ICONS[name];
  if (!iconPath) {
    // Fallback for unknown/emoji icons — render a generic office icon
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={`text-[#86868b] ${className}`}>
        {SVG_ICONS.gear}
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={`text-[#1d1d1f] ${className}`}>
      {iconPath}
    </svg>
  );
}

export default function GeneratedProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useShop();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const [qty, setQty] = useState(1);
  const [cartToast, setCartToast] = useState(false);
  const inquiryRef = useRef(null);
  const [showInquiry, setShowInquiry] = useState(false);

  // Showcase option selections (shared between showcase & rental sections)
  const [showcaseSelections, setShowcaseSelections] = useState({ opts: {}, groups: [] });

  // Admin editor states
  const [editMode, setEditMode] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishMallType, setPublishMallType] = useState("");
  const [toast, setToast] = useState("");

  const isAdmin = page?._is_admin;

  useEffect(() => {
    setLoading(true);
    api.get(`/api/product-pages/${slug}`)
      .then(r => { setPage(r.data); })
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  /* Track scroll for parallax */
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4" style={{ borderColor: "var(--ink-100)" }} />
            <div className="absolute inset-0 rounded-full border-4 animate-spin" style={{ borderTopColor: "var(--rent-500)", borderRightColor: "transparent", borderBottomColor: "transparent", borderLeftColor: "transparent" }} />
          </div>
          <div className="space-y-2 text-center">
            <div className="h-3 w-28 rounded-full animate-pulse" style={{ background: "var(--ink-100)" }} />
            <div className="h-2.5 w-20 rounded-full animate-pulse mx-auto" style={{ background: "var(--ink-75)" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <div className="w-20 h-20 rounded-full grid place-items-center mb-6" style={{ background: "var(--ink-50)" }}>
          <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "var(--ink-300)" }} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6" />
          </svg>
        </div>
        <p className="text-xl mb-4" style={{ color: "var(--ink-500)" }}>페이지를 찾을 수 없습니다</p>
        <Link to="/products" className="inline-flex items-center gap-1 font-semibold hover:underline" style={{ color: "var(--rent-700)" }}>
          제품 목록으로 →
        </Link>
      </div>
    );
  }

  // ── 중복 안내 섹션 필터: 월 렌탈료 프로그램/구매 가격 안내 페이지에서 이미 제공되는
  //    "배송 안내" / "교환 및 서비스 안내" / "반품 안내" / "합리적인 렌탈 프로그램" / "구매 가격 안내" 등 안내성 섹션은 상세에서 숨김.
  const _DUP_GUIDE_RE = /(배송\s*안내|배송\s*방법|배송\s*및\s*반품|교환\s*(및|·)?\s*(서비스|반품)|반품\s*안내|A\/?S\s*안내|서비스\s*안내|합리적인\s*렌탈\s*프로그램|월\s*렌탈료\s*$|구매\s*가격\s*안내|구매\s*안내|delivery|shipping|return\s*policy|exchange\s*policy)/i;
  const _isDupGuide = (sec) => {
    if (!sec) return false;
    const title = `${sec.title || ""}`.trim();
    const kicker = `${sec.kicker || ""}`.trim();
    if (!title && !kicker && !sec.content) return false;
    // 제목/키커가 정확히 안내 섹션이면 차단
    if (_DUP_GUIDE_RE.test(title) || _DUP_GUIDE_RE.test(kicker)) return true;
    // kicker + title 결합 안내 패턴 차단
    const combined = `${kicker} ${title}`;
    if (_DUP_GUIDE_RE.test(combined)) return true;
    // kicker="구매 가격" + title="안내" 같은 분리형도 차단
    if (/구매\s*가격/.test(kicker) && /^안내$/.test(title)) return true;
    if (/월\s*렌탈료/.test(kicker) && /합리적인\s*렌탈\s*프로그램/.test(title)) return true;
    return false;
  };
  const sections = (Array.isArray(page.sections_json) ? page.sections_json : [])
    .filter((s) => !_isDupGuide(s));
  const items = page.items || [];
  const theme = page.theme || "apple";

  /* Hero CTA customization from specs_json._hero_visual */
  const heroVisual = (typeof page.specs_json === "object" && page.specs_json?._hero_visual) || {};
  const heroCta = {
    text: heroVisual.cta_text || "",
    link: heroVisual.cta_link || "",
    showPrice: heroVisual.hide_price !== true,
    showCta: heroVisual.hide_cta !== true,
  };

  /* Theme-based style overrides */
  const THEME_STYLES = {
    apple:     { bg: "bg-white", heroBg: "bg-gradient-to-b from-[#fbfbfd] via-white to-white", titleColor: "text-[#1d1d1f]", subColor: "text-[#86868b]", accent: "#0071e3" },
    corporate: { bg: "bg-white", heroBg: "bg-gradient-to-r from-blue-900 to-blue-700", titleColor: "text-white", subColor: "text-blue-200", accent: "#1e40af" },
    bold:      { bg: "bg-white", heroBg: "bg-gradient-to-r from-red-600 to-orange-500", titleColor: "text-white", subColor: "text-orange-100", accent: "#dc2626" },
    warm:      { bg: "bg-amber-50/30", heroBg: "bg-gradient-to-b from-green-50 to-amber-50", titleColor: "text-green-900", subColor: "text-green-700", accent: "#059669" },
    tech:      { bg: "bg-[#0a0a0a]", heroBg: "bg-[#0a0a0a]", titleColor: "text-white", subColor: "text-cyan-300", accent: "#00f0ff" },
    catalog:   { bg: "bg-gray-50", heroBg: "bg-white", titleColor: "text-gray-900", subColor: "text-gray-500", accent: "#2563eb" },
    luxury:    { bg: "bg-black", heroBg: "bg-gradient-to-b from-gray-900 to-black", titleColor: "text-white", subColor: "text-[#c9a96e]", accent: "#c9a96e" },
  };
  const ts = THEME_STYLES[theme] || THEME_STYLES.apple;

  /* ── Category fallback image mapping ── */
  const CATEGORY_FALLBACK = {
    "고속인쇄기": "/api/category-images/1.고속인쇄기.webp",
    "디지털인쇄기": "/api/category-images/1.고속인쇄기.webp",
    "대형복합기": "/api/category-images/2.대형 복합기.webp",
    "사무용복합기": "/api/category-images/2.대형 복합기.webp",
    "복합기": "/api/category-images/2.대형 복합기.webp",
    "소형복합기": "/api/category-images/3.소형 복합기.webp",
    "프린터": "/api/category-images/4.프린터.webp",
    "토너소모품": "/api/category-images/5.토너 소모품.webp",
    "토너": "/api/category-images/5.토너 소모품.webp",
  };
  const _normCat = (s) => (s || "").replace(/\s+/g, "").trim();
  const _resolveFallback = () => {
    const candidates = [page.store_category, page.category, page.sub_category]
      .map(_normCat)
      .filter(Boolean);
    for (const c of candidates) {
      if (CATEGORY_FALLBACK[c]) return CATEGORY_FALLBACK[c];
      // partial match (e.g. "사무용컬러복합기" → contains "복합기")
      for (const k of Object.keys(CATEGORY_FALLBACK)) {
        if (c.includes(k)) return CATEGORY_FALLBACK[k];
      }
    }
    // generic default
    return "/api/category-images/2.대형 복합기.webp";
  };
  const heroImage = page.hero_image || _resolveFallback();
  const isFallbackImage = !page.hero_image && heroImage;

  /* ── Shopping helpers ── */
  const pagePrice = page.price || page.rental_price || 0;
  const isRental = page.sale_type === "rental" || page.sale_type === "both";
  const canPurchase = !isRental && pagePrice > 0;
  const naverPaySaleable = page.sale_type === "sale" && Number(page.price || 0) > 0 && page.mall_type !== "b2b";
  const ctaLabel = heroCta.text || (isRental ? "견적 문의" : (pagePrice > 0 ? "구매하기" : "가격 문의"));

  const handleInquiry = () => {
    if (ctaToKakao()) return;
    setShowInquiry(true);
    setTimeout(() => {
      inquiryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleAddToCart = async () => {
    if (ctaToKakao()) return;
    if (isRental) { handleInquiry(); return; }
    if (!user) { navigate("/login"); return; }
    const orderQty = normalizeOrderQuantity(qty);
    setQty(orderQty);
    const res = await addToCart("page", page.id, orderQty);
    if (!res?.error) { setCartToast(true); setTimeout(() => setCartToast(false), 3000); }
  };

  const handleBuyNow = async () => {
    if (ctaToKakao()) return;
    if (isRental) { handleInquiry(); return; }
    if (!user) { navigate("/login"); return; }
    const orderQty = normalizeOrderQuantity(qty);
    setQty(orderQty);
    const res = await addToCart("page", page.id, orderQty);
    if (!res?.error) navigate("/checkout");
  };

  const handlePrimaryCta = () => {
    if (isRental || pagePrice <= 0) {
      handleInquiry();
      return;
    }
    handleBuyNow();
  };

  /* ── Admin helpers ── */
  const handleEditorSaved = async () => {
    // Reload page data after save from full editor (use ID, not slug — slug may have changed)
    try {
      const r = await api.get(`/api/admin/product-pages/${page.id}`);
      setPage(r.data);
      // Update URL if slug changed
      if (r.data.slug && r.data.slug !== slug) {
        window.history.replaceState(null, '', `/product/${r.data.slug}`);
      }
    } catch {}
    setEditMode(false);
  };

  const handlePublish = async () => {
    if (!page?.id) return;
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    if (!publishMallType) return;
    setPublishing(true);
    try {
      await api.put(`/api/admin/product-pages/${page.id}`, { status: "published", mall_type: publishMallType });
      setToast("✅ 발행 완료!");
      const r = await api.get(`/api/product-pages/${slug}`);
      setPage(r.data);
    } catch (e) {
      setToast("❌ 발행 실패: " + (e.response?.data?.error || e.message));
    } finally { setPublishing(false); setShowPublishModal(false); setPublishMallType(""); }
  };

  const handleUnpublish = async () => {
    if (!page?.id) return;
    if (!window.confirm("이 페이지를 초안 상태로 변경하시겠습니까?")) return;
    try {
      await api.put(`/api/admin/product-pages/${page.id}`, { status: "draft" });
      setToast("페이지가 비공개 처리되었습니다");
      const r = await api.get(`/api/product-pages/${slug}`);
      setPage(r.data);
    } catch (e) {
      setToast("❌ " + (e.response?.data?.error || e.message));
    }
  };

  const handleRegenerate = async () => {
    if (!page?.id) return;
    if (!window.confirm("AI로 이 페이지를 다시 생성하시겠습니까? 기존 내용이 대체됩니다.")) return;
    setToast("🔄 AI 재생성 중...");
    try {
      await api.post(`/api/admin/product-pages/${page.id}/regenerate`);
      setToast("✅ 재생성 완료!");
      const r = await api.get(`/api/product-pages/${slug}`);
      setPage(r.data);
    } catch (e) {
      setToast("❌ 재생성 실패: " + (e.response?.data?.error || e.message));
    }
  };

  return (
    <>
      <SEO
        title={page.seo_title || `${page.title} | 복사기 복합기 - ${BRAND.name}`}
        description={page.seo_description || page.subtitle}
        keywords={`${page.title}, ${page.brand || ''} 복사기, 복합기 ${page.category || ''}, 복사기 렌탈, 복합기 가격`}
        path={`/p/${slug}`}
        product={{
          name: page.title,
          brand: page.brand,
          category: page.category || "복사기",
          image: page.hero_image,
          price: page.price || 0,
          rentalPrice: page.rental_price || 0,
          id: page.id,
          sku: `IK-${page.id || "0"}`,
        }}
        breadcrumbs={(() => {
          const isLocal = (page.product_code || "").startsWith("IKL-");
          if (isLocal && page.district && page.district !== "전국") {
            const [region, ...rest] = page.district.split(" ");
            const dist = rest.join(" ");
            const bc = [
              { name: "홈", url: "/" },
              { name: "복합기 렌탈", url: "/rental" },
              { name: region, url: `/rental/${region}` },
            ];
            if (dist) bc.push({ name: dist, url: `/rental/${region}/${dist}` });
            bc.push({ name: page.title });
            return bc;
          }
          return [
            { name: "홈", url: "/" },
            { name: "스토어", url: "/store" },
            { name: page.title },
          ];
        })()}
        areaServed={(page.product_code || "").startsWith("IKL-") ? (page.district || "") : ""}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] bg-[#1d1d1f] text-white px-5 py-2.5 rounded-xl shadow-2xl text-sm font-medium animate-[fadeUp_0.3s_ease] max-w-md">
          {toast}
          <button onClick={() => setToast("")} className="ml-3 text-white/50 hover:text-white">✕</button>
        </div>
      )}
      {toast && setTimeout(() => setToast(""), 3000) && null}

      {/* ── Publish Modal (발행 위치 선택) ── */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[380px] p-6 animate-[fadeUp_0.2s_ease]">
            <h3 className="text-lg font-bold text-gray-900 mb-1">📍 발행 위치 선택</h3>
            <p className="text-sm text-gray-500 mb-5">발행할 스토어를 선택해주세요.</p>
            <div className="space-y-3">
              {[
                { value: "general", label: "일반몰", desc: "일반 스토어에 공개", color: "green" },
                { value: "b2b", label: "사업자몰", desc: "B2B 사업자 전용 스토어에 공개", color: "purple" },
              ].map(opt => (
                <button key={opt.value} onClick={() => setPublishMallType(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                    publishMallType === opt.value
                      ? opt.color === "green" ? "border-green-500 bg-green-50" : "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      publishMallType === opt.value
                        ? opt.color === "green" ? "border-green-500" : "border-purple-500"
                        : "border-gray-300"
                    }`}>
                      {publishMallType === opt.value && (
                        <div className={`w-2.5 h-2.5 rounded-full ${opt.color === "green" ? "bg-green-500" : "bg-purple-500"}`} />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setShowPublishModal(false); setPublishMallType(""); }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                취소
              </button>
              <button onClick={confirmPublish} disabled={!publishMallType || publishing}
                className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition disabled:opacity-40">
                {publishing ? "발행 중..." : "🚀 발행"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin Floating Toolbar ── */}
      {isAdmin && (
        <div className="fixed top-14 left-0 right-0 z-[60] bg-gradient-to-r from-[#1d1d1f] to-[#2d2d30] shadow-xl border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
            {/* Status badge */}
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              page.status === "published" ? "bg-green-500/20 text-green-400" :
              page.status === "draft" ? "bg-yellow-500/20 text-yellow-400" :
              "bg-gray-500/20 text-gray-400"
            }`}>
              {page.status === "published" ? "발행됨" : page.status === "draft" ? "초안" : page.status}
            </span>

            <span className="text-white/40">|</span>
            <span className="text-white/70 text-xs truncate max-w-[200px]">ID: {page.id}</span>

            <div className="flex-1" />

            {/* Edit toggle */}
            <button onClick={() => setEditMode(!editMode)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${editMode ? "bg-blue-600 text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
              {editMode ? "✏️ 편집 중" : "✏️ 편집"}
            </button>

            {/* Presentation */}
            <button onClick={() => setPresenting(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 transition shadow">
              📺 프레젠테이션
            </button>

            {/* Regenerate */}
            <button onClick={handleRegenerate}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-white/80 hover:bg-white/20 transition">
              🤖 AI 재생성
            </button>

            {/* Publish / Unpublish */}
            {page.status === "draft" ? (
              <button onClick={handlePublish} disabled={publishing}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50">
                {publishing ? "발행 중..." : "🚀 발행"}
              </button>
            ) : page.status === "published" ? (
              <button onClick={handleUnpublish}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition">
                비공개
              </button>
            ) : null}
            {page.mall_type && (
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${page.mall_type === 'b2b' ? 'bg-purple-500/30 text-purple-300' : 'bg-green-500/30 text-green-300'}`}>
                {page.mall_type === 'b2b' ? '사업자몰' : '일반몰'}
              </span>
            )}

            {/* Back to admin */}
            <Link to="/admin" className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-white/80 hover:bg-white/20 transition">
              ← 관리자
            </Link>
          </div>
        </div>
      )}

      {/* ── Admin Full-screen Editor (Velog-style) ── */}
      {isAdmin && editMode && (
        <div className="fixed inset-0 z-[100] bg-white overflow-auto">
          <ProductPageEditor
            pageId={page.id}
            onClose={() => setEditMode(false)}
            onSaved={handleEditorSaved}
            setToast={setToast}
          />
        </div>
      )}

      {/* ── Presentation Mode ── */}
      {presenting && (
        <div className="fixed inset-0 z-[9999] bg-white overflow-y-auto" style={{ scrollBehavior: "smooth" }}>
          <div className="fixed top-4 right-4 z-[10000] flex gap-2 opacity-0 hover:opacity-100 transition-opacity duration-300">
            <button onClick={() => {
              const el = document.documentElement;
              if (el.requestFullscreen) el.requestFullscreen();
              else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
            }}
              className="px-3 py-2 bg-black/60 text-white rounded-xl text-xs font-medium backdrop-blur-sm hover:bg-black/80 transition">
              🖥️ 전체화면
            </button>
            <button onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); setPresenting(false); }}
              className="px-3 py-2 bg-red-600/80 text-white rounded-xl text-xs font-medium backdrop-blur-sm hover:bg-red-700 transition">
              ✕ 닫기
            </button>
          </div>
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] opacity-0 hover:opacity-100 transition-opacity duration-300">
            <span className="text-xs text-white bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">ESC로 닫기</span>
          </div>
          {/* Re-render page content without admin bars */}
          <div className={`${ts.bg} overflow-x-hidden`}>
            {/* Hero */}
            <div className={`${ts.heroBg} py-20 md:py-28 text-center`}>
              {page.brand && <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: ts.accent }}>{page.brand}</p>}
              <h1 className={`text-4xl md:text-6xl font-bold ${ts.titleColor} tracking-tight leading-tight px-6`}>{page.model_name || page.title}</h1>
              {page.subtitle && <p className={`text-xl md:text-2xl ${ts.subColor} mt-4 px-6`}>{page.subtitle}</p>}
              {heroImage && (
                <div className="mt-10 flex justify-center px-6">
                  <img src={heroImage} alt={page.title} className="max-h-[500px] object-contain" loading="lazy" />
                </div>
              )}
            </div>
            {sections.map((sec, si) => <SectionRenderer key={si} section={sec} index={si} allSections={sections} heroImage={heroImage} productTitle={page.model_name || page.title} productCode={page.product_code} showcaseSelections={showcaseSelections} onShowcaseChange={setShowcaseSelections} isRental={isRental} canPurchase={canPurchase} quantity={qty} onQuantityChange={setQty} onInquiry={handleInquiry} onBuyNow={handlePrimaryCta} ctaLabel={ctaLabel} />)}
          </div>
        </div>
      )}
      {presenting && <PresentationEscHandler onExit={() => { if (document.fullscreenElement) document.exitFullscreen(); setPresenting(false); }} />}

      {/* ── Global animation keyframes (injected once) ── */}
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideLeft { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideRight { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 20px rgba(0,113,227,0.15); } 50% { box-shadow: 0 0 40px rgba(0,113,227,0.35); } }
        .reveal { opacity: 0; transform: translateY(40px); transition: opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1); }
        .reveal.visible { opacity: 1; transform: translateY(0); }
        .reveal-scale { opacity: 0; transform: scale(0.9); transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1); }
        .reveal-scale.visible { opacity: 1; transform: scale(1); }
        .hero-title { animation: fadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
        .hero-sub { animation: fadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .hero-cta { animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.65s both; }
        .hero-img { animation: scaleIn 1.2s cubic-bezier(0.16,1,0.3,1) 0.1s both; }
        .hero-divider { background: linear-gradient(90deg, transparent, #d2d2d7 30%, #d2d2d7 70%, transparent); }
      `}</style>

      <div className={`${ts.bg} overflow-x-hidden ${isAdmin ? "pt-10" : ""}`}>
        {/* Admin draft banner */}
        {isAdmin && page.status === "draft" && (
          <div className="bg-yellow-50 border-b border-yellow-200 py-2 px-4 text-center text-sm text-yellow-700">
            ⚠️ 이 페이지는 <strong>초안</strong> 상태입니다. 일반 사용자에게 보이지 않습니다.
          </div>
        )}
        {/* ── Hero Section (2026 redesign · rent/sale accent) ─────────── */}
        <section
          className="relative overflow-hidden"
          style={{
            background: theme === "apple" || theme === "catalog"
              ? (isRental
                  ? "linear-gradient(180deg, var(--rent-50) 0%, #fff 92%)"
                  : "linear-gradient(180deg, var(--sale-50) 0%, #fff 92%)")
              : undefined,
          }}
        >
          {/* Theme-aware background (only for non-default themes) */}
          {theme !== "apple" && theme !== "catalog" && <div className={`absolute inset-0 ${ts.heroBg}`} />}
          {/* deco glow */}
          <div
            className="absolute pointer-events-none"
            style={{
              top: -120, right: -180, width: 600, height: 600,
              background: isRental
                ? "radial-gradient(circle, rgba(31,182,131,0.18) 0%, transparent 65%)"
                : "radial-gradient(circle, rgba(10,107,255,0.16) 0%, transparent 65%)",
              transform: `translateY(${scrollY * -0.15}px)`,
            }}
          />

          <div className="relative max-w-5xl mx-auto px-6 pt-24 md:pt-36 pb-12 text-center">
            {heroImage && (
              <div className="hero-img mb-10 relative">
                <img src={heroImage} alt={page.title}
                  className="mx-auto max-h-[420px] object-contain drop-shadow-2xl"
                  loading="lazy"
                  style={{ transform: `translateY(${scrollY * 0.08}px)` }} />
                {isFallbackImage && (
                  <p className="text-center text-xs mt-3" style={{ color: "var(--ink-400)" }}>* 대표 이미지이며, 실제 제품과 다를 수 있습니다</p>
                )}
              </div>
            )}

            {/* Tag chips: sale type + brand + product code */}
            <div className="flex items-center justify-center gap-2 flex-wrap mb-4">
              <span className={`tag-pill ${isRental ? "tag-rent" : "tag-sale"}`}>
                {isRental ? "월 렌탈" : "구매"}
              </span>
              {page.brand && (
                <span className="text-[12px] font-semibold tracking-widest uppercase" style={{ color: isRental ? "var(--rent-700)" : "var(--sale-700)" }}>
                  {page.brand}
                </span>
              )}
              {page.product_code && (
                <span className="tag-pill tag-warn inline-flex items-center gap-1.5">
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>
                  {page.product_code}
                </span>
              )}
            </div>

            {/* Product model/name (BIG title) */}
            <h1
              className={`hero-title font-bold tracking-tight leading-[1.04] max-w-4xl mx-auto ${ts.titleColor}`}
              style={{
                fontSize: "clamp(34px, 5.4vw, 60px)",
                fontWeight: 800,
                letterSpacing: "-0.035em",
              }}
            >
              {(theme === "apple" || theme === "catalog") && (page.model_name || page.title) ? (
                <span style={{
                  background: isRental
                    ? "linear-gradient(120deg, var(--rent-500), var(--rent-700))"
                    : "linear-gradient(120deg, var(--sale-500), var(--sale-700))",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
                  {page.model_name || page.title}
                </span>
              ) : (
                page.model_name || page.title || "제품명"
              )}
            </h1>

            {/* Subtitle / description */}
            {page.subtitle && (
              <p className={`hero-sub mt-5 text-lg md:text-2xl ${ts.subColor} max-w-2xl mx-auto leading-relaxed font-light`}>
                {page.subtitle}
              </p>
            )}

            <div className="hero-cta mt-10 flex flex-col items-center gap-4">
              {pagePrice > 0 && (
                <span
                  className="num-display"
                  style={{
                    color: isRental ? "var(--rent-700)" : "var(--sale-700)",
                    fontSize: "clamp(28px, 4vw, 40px)",
                  }}
                >
                  {isRental ? "월 " : ""}{pagePrice.toLocaleString()}<span className="text-[15px] font-semibold ml-1" style={{ color: "var(--ink-400)" }}>{isRental ? "원~" : "원"}</span>
                </span>
              )}
              {canPurchase && (
                <QuantityInput value={qty} onChange={setQty} />
              )}
              {naverPaySaleable && (
                <NaverPayButton
                  productType="page"
                  productId={page.id}
                  quantity={qty}
                  className="w-full max-w-[420px]"
                />
              )}
              <div className="flex items-center gap-3 flex-wrap justify-center">
                <button
                  onClick={handlePrimaryCta}
                  className={`${isRental ? "btn-rent" : "btn-sale"} group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold transition-all duration-300`}
                >
                  {ctaLabel}
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                </button>
                {!isRental && (
                  <button
                    onClick={handleAddToCart}
                    className="btn-ghost-sale inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold transition-all duration-300"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6"/></svg>
                    장바구니
                  </button>
                )}
                {!isRental && (
                  <button
                    onClick={handleInquiry}
                    className="btn-ghost-rent inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold transition-all duration-300"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>
                    {heroCta.text || "견적 문의"}
                  </button>
                )}
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap justify-center gap-x-7 gap-y-3 mt-8 pt-6 border-t max-w-2xl mx-auto" style={{ borderColor: "var(--line-soft)" }}>
                {(isRental ? [
                  { ic: "shield", lbl: "초기 비용 부담 없음" },
                  { ic: "tools",  lbl: "무상 유지보수" },
                  { ic: "truck",  lbl: "전국 무료 설치" },
                ] : [
                  { ic: "truck",  lbl: "전국 무료 배송" },
                  { ic: "shield", lbl: "본사 1년 보증" },
                  { ic: "tools",  lbl: "전국 출장 AS" },
                ]).map((it, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg grid place-items-center"
                      style={{
                        background: isRental ? "var(--rent-100)" : "var(--sale-100)",
                        color: isRental ? "var(--rent-700)" : "var(--sale-700)",
                      }}
                    >
                      {it.ic === "shield" && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V6l9-4z"/><path d="M9 12l2 2 4-4"/></svg>}
                      {it.ic === "tools"  && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77A6 6 0 0114 13.5l-7.5 7.5-3-3 7.5-7.5A6 6 0 0118 4l-3.3 2.3z"/></svg>}
                      {it.ic === "truck"  && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="1" y="6" width="14" height="11" rx="1"/><path d="M15 9h4l3 4v4h-7"/><circle cx="6" cy="20" r="2"/><circle cx="18" cy="20" r="2"/></svg>}
                    </div>
                    <span className="text-[13px] font-semibold" style={{ color: "var(--ink-700)" }}>{it.lbl}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Thin divider */}
            <div className="hero-divider h-px w-full max-w-3xl mx-auto mt-12 opacity-60" />
          </div>
        </section>

        {/* ── Reviews (anonymous) — above key features ─────── */}
        {page.id && (
          <ReviewSection productType="page" productId={page.id} accent={isRental ? "rent" : "sale"} />
        )}

        {/* ── Dynamic Sections ──────────────────────────── */}
        {sections.map((sec, i) => (
          <SectionRenderer key={i} section={sec} index={i} allSections={sections} heroImage={heroImage} productTitle={page.model_name || page.title} productCode={page.product_code} showcaseSelections={showcaseSelections} onShowcaseChange={setShowcaseSelections} isRental={isRental} canPurchase={canPurchase} quantity={qty} onQuantityChange={setQty} onInquiry={handleInquiry} onBuyNow={handlePrimaryCta} ctaLabel={ctaLabel} />
        ))}

        {/* ── Compatible Products ─────────────────────── */}
        {page.compatible_products && page.compatible_products.length > 0 && (
          <CompatibleProductsSection products={page.compatible_products} currentCategory={page.store_category} />
        )}

        {/* ── Product Items (if no product_card sections) ── */}
        {items.length > 0 && !sections.some(s => s.type === "product_card") && (
          <ItemsGrid items={items} accent={isRental ? "rent" : "sale"} />
        )}

        {/* ── Product policies ─────────────────────────── */}
        <ProductPolicyTables page={page} />

        {/* ── Footer CTA ───────────────────────────────── */}
        {!sections.some(s => s.type === "cta") && heroCta.showCta && <DefaultCTA heroCta={heroCta} isRental={isRental} canPurchase={canPurchase} quantity={qty} onQuantityChange={setQty} onInquiry={handleInquiry} onBuyNow={handlePrimaryCta} ctaLabel={ctaLabel} />}

        {/* ── Inline Inquiry Form ────────────────────── */}
        <div ref={inquiryRef}>
          {showInquiry && (
            <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#f5f5f7]">
              <div className="max-w-xl mx-auto px-4">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-3">가격 문의</h2>
                  <p className="text-[#86868b] text-sm md:text-base">아래 정보를 입력하시면 빠르게 견적을 보내드립니다.</p>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <InlineInquiryForm
                    product={(() => {
                      const productName = page.model_name || page.title;
                      // 선택된 옵션 정보 포함
                      let optionInfo = "";
                      if (showcaseSelections?.groups?.length > 0) {
                        const { opts, groups } = showcaseSelections;
                        const parts = [];
                        groups.forEach((g, gi) => {
                          const oi = opts[gi];
                          if (oi == null) return;
                          const opt = (g.options || [])[oi];
                          if (opt) parts.push(`${g.group_name}: ${opt.name}`);
                        });
                        if (parts.length > 0) optionInfo = ` (${parts.join(" / ")})`;
                      }
                      return {
                        id: page.id,
                        name: (page.product_code ? `[${page.product_code}] ` : '') + productName + optionInfo,
                        image_url: heroImage,
                        rental_price: page.rental_price || 0,
                        price: page.price || 0,
                        _product_type: "page",
                        source: "store",
                        product_code: page.product_code || "",
                      };
                    })()}
                    onSuccess={() => {}}
                  />
                </div>
              </div>
            </section>
          )}
        </div>

        {/* ── Breadcrumb + QR ─────────────────────────── */}
        <div className="border-t border-gray-200/80 bg-[#f5f5f7] py-4">
          <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <nav className="flex items-center gap-2 text-[12px] text-[#6e6e73] min-w-0 overflow-hidden">
              <Link to="/" className="hover:text-[#1d1d1f] transition-colors shrink-0">홈</Link>
              <span className="text-gray-300 shrink-0">›</span>
              <Link to="/products" className="hover:text-[#1d1d1f] transition-colors shrink-0">제품</Link>
              <span className="text-gray-300 shrink-0">›</span>
              {page.brand && (
                <>
                  <Link to={`/products?brand=${page.brand}`} className="hover:text-[#1d1d1f] transition-colors shrink-0">{page.brand}</Link>
                  <span className="text-gray-300 shrink-0">›</span>
                </>
              )}
              <span className="text-[#1d1d1f] font-medium truncate">{page.title}</span>
            </nav>
            <div className="flex items-center gap-3 print:hidden">
              <PageQR url={`${BRAND.url}/p/${slug}`} size={48} />
              <div className="text-right text-[10px] text-[#86868b]">
                <p className="font-bold text-[#1d1d1f] text-[13px]">1600-7542</p>
                <p>평일 09:00~18:00</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cart Toast ────────────────────────────────── */}
      {cartToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] bg-green-600 text-white px-6 py-3 rounded-xl shadow-2xl text-sm font-medium animate-[fadeUp_0.3s_ease] flex items-center gap-3">
          ✅ 장바구니에 담았습니다
          <Link to="/cart" className="underline font-bold hover:text-green-100">바로가기 →</Link>
          <button onClick={() => setCartToast(false)} className="ml-2 text-white/60 hover:text-white">✕</button>
        </div>
      )}

      {/* ── Floating Bottom Purchase Bar (2026 redesign) ─────────── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl print:hidden mob-bottom-bar"
        style={{
          background: "rgba(255,255,255,0.92)",
          borderTop: "1px solid var(--line-soft)",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0))",
        }}
      >
        <div className="max-w-[1180px] mx-auto flex items-center justify-between gap-3">
          {/* product summary */}
          <div className="flex items-center gap-3 min-w-0">
            {heroImage && (
              <img src={heroImage} alt="" className="w-12 h-12 rounded-lg object-contain hidden sm:block" style={{ background: "var(--ink-25)" }} onError={(e) => { e.target.style.display = "none"; }} />
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-bold truncate" style={{ color: "var(--ink-900)" }}>{page.model_name || page.title}</p>
              {pagePrice > 0 && (
                <span
                  className="num-display text-[16px] sm:text-[18px] whitespace-nowrap"
                  style={{ color: isRental ? "var(--rent-700)" : "var(--sale-700)", letterSpacing: "-0.02em" }}
                >
                  {isRental ? `월 ${pagePrice.toLocaleString()}` : pagePrice.toLocaleString()}
                  <span className="text-[12px] font-semibold ml-0.5" style={{ color: "var(--ink-400)" }}>{isRental ? "원~" : "원"}</span>
                </span>
              )}
            </div>
          </div>

          {/* actions */}
          <div className="flex items-center gap-2 shrink-0">
            {canPurchase && (
              <QuantityInput value={qty} onChange={setQty} compact />
            )}

            {isRental ? (
              <button
                onClick={handleInquiry}
                className="btn-rent inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2.5 text-[13px] sm:text-[14px] font-semibold whitespace-nowrap"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>
                {ctaLabel}
              </button>
            ) : (
              <>
                <button
                  onClick={handleAddToCart}
                  className="btn-ghost-sale hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[14px] font-semibold whitespace-nowrap"
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6"/></svg>
                  담기
                </button>
                <button
                  onClick={handleBuyNow}
                  className="btn-sale inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2.5 text-[13px] sm:text-[14px] font-semibold whitespace-nowrap"
                >
                  구매하기
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Floating PDF Button ────────────────────────── */}
      <button
        onClick={() => setShowPdfPreview(true)}
        className="fixed bottom-20 right-8 z-40 flex items-center gap-2 px-5 py-3.5 bg-[#1d1d1f] text-white rounded-full shadow-xl hover:bg-[#333] transition-all duration-300 hover:scale-105 hover:shadow-2xl print:hidden group"
        style={{ animation: "glow 3s ease-in-out infinite" }}
        title="제품 자료 PDF 다운로드"
      >
        <svg className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        PDF 다운로드
      </button>

      {/* ── PDF Preview Modal ──────────────────────────── */}
      {showPdfPreview && (
        <ProductPdfModal page={page} items={items} sections={sections} onClose={() => setShowPdfPreview(false)} />
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════
   Items Grid — accent-aware lineup
   ═══════════════════════════════════════════════════ */
function ItemsGrid({ items, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  return (
    <section ref={ref} className="py-20" style={{ background: "var(--ink-25)" }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className={`text-center mb-12 reveal ${visible ? "visible" : ""}`}>
          <div className="t-eyebrow mb-2.5" style={{ color: a.primaryDark }}>라인업</div>
          <h2 className="t-headline text-[28px] sm:text-[36px] md:text-[40px] mb-3" style={{ fontWeight: 700 }}>
            비즈니스에 최적화된 제품군
          </h2>
          <p className="text-lg" style={{ color: "var(--ink-500)" }}>가장 적합한 모델을 만나보세요</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, j) => (
            <div key={item.id || j}
              className={`card p-7 text-center transition-all duration-500 group reveal ${visible ? "visible" : ""}`}
              style={{ background: "white", border: "1px solid var(--line-soft)", ...stagger(j, 0.1) }}>
              {item.image_url ? (
                <img src={item.image_url} alt={item.product_name}
                  className="h-48 mx-auto object-contain mb-6 transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="h-48 flex items-center justify-center mb-6">
                  <div className="w-28 h-28 rounded-full grid place-items-center" style={{ background: a.soft, border: `1px solid ${a.softer}` }}>
                    <SvgIcon name="printer" size={48} style={{ color: a.border }} />
                  </div>
                </div>
              )}
              <h3 className="text-xl font-bold mb-2" style={{ color: "var(--ink-900)" }}>{item.product_name}</h3>
              {item.model && <p className="text-sm mb-3 font-mono" style={{ color: "var(--ink-400)" }}>{item.model}</p>}
              <p className="num-display text-lg" style={{ color: a.primaryDark }}>
                {item.price ? `${Number(item.price).toLocaleString()}원` : (item.rental_price ? `월 ${Number(item.rental_price).toLocaleString()}원` : "")}
              </p>
              {item.description && (
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-500)" }}>{item.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════
   Default CTA — with subtle pulse animation
   ═══════════════════════════════════════════════════ */
function DefaultCTA({ heroCta = {}, isRental = false, canPurchase = false, quantity = 1, onQuantityChange, onInquiry, onBuyNow, ctaLabel = "" }) {
  const label = ctaLabel || (isRental ? "견적 문의" : "구매하기");
  const [ref, visible] = useReveal();
  const handleClick = isRental && onInquiry ? onInquiry : (onBuyNow || (() => window.scrollTo({ top: 0, behavior: 'smooth' })));
  return (
    <section
      ref={ref}
      className="py-24 text-white relative overflow-hidden"
      style={{
        background: isRental
          ? "linear-gradient(135deg, var(--rent-700) 0%, var(--rent-900) 100%)"
          : "linear-gradient(135deg, var(--sale-700) 0%, var(--sale-900) 100%)",
      }}
    >
      <div className="max-w-[920px] mx-auto px-6 text-center relative">
        <h2
          className={`reveal ${visible ? "visible" : ""}`}
          style={{
            fontSize: "clamp(28px, 4.5vw, 48px)",
            fontWeight: 800,
            letterSpacing: "-0.035em",
            lineHeight: 1.1,
            marginBottom: 16,
          }}
        >
          {isRental ? "지금 통화 한 번이면, 내일 설치됩니다." : "지금 결제하면, 내일 도착합니다."}
        </h2>
        <p
          className={`text-[15px] sm:text-[17px] leading-relaxed mb-8 max-w-[560px] mx-auto reveal ${visible ? "visible" : ""}`}
          style={{ color: "rgba(255,255,255,0.7)", transitionDelay: "0.15s" }}
        >
          {isRental
            ? "전문 매니저가 사용 환경에 맞춰 옵션·기간·요금을 직접 제안해드립니다."
            : "최고의 사무 솔루션을 합리적인 가격에 만나보세요. 본사 정품 + 1년 보증 + 무료 배송 포함."}
        </p>
        {canPurchase && onQuantityChange && (
          <div className={`flex justify-center mb-4 reveal ${visible ? "visible" : ""}`} style={{ transitionDelay: "0.25s" }}>
            <QuantityInput value={quantity} onChange={onQuantityChange} variant="dark" />
          </div>
        )}
        <div className={`flex gap-3 justify-center flex-wrap reveal ${visible ? "visible" : ""}`} style={{ transitionDelay: "0.3s" }}>
          <a
            href="tel:1600-7542"
            className="inline-flex items-center gap-2 rounded-full font-semibold"
            style={{
              background: "white",
              color: isRental ? "var(--rent-900)" : "var(--sale-900)",
              padding: "16px 24px", fontSize: 16,
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>
            1600-7542 즉시 통화
          </a>
          <button
            onClick={handleClick}
            className="inline-flex items-center gap-2 rounded-full font-semibold"
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              color: "white",
              padding: "16px 24px", fontSize: 16,
              backdropFilter: "blur(10px)",
            }}
          >
            {label}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════
   PDF 다운로드 모달 — A4 담백한 구매 담당자용
   원소스 멀티유즈: PrintPdfShell 공유 컴포넌트 사용
   ═══════════════════════════════════════════════════ */
function ProductPdfModal({ page, items, sections, onClose }) {
  const specSections = sections.filter(s => s.type === "spec_table");
  const featureSections = sections.filter(s => s.type === "feature_highlight");
  const qrUrl = `${BRAND.url}/p/${page.slug}`;
  const isRental = page.sale_type === "rental" || page.sale_type === "both";
  const accentColor = isRental ? "var(--rent-700)" : "var(--sale-700)";

  return (
    <PdfPreviewModal containerId="product-pdf-content" onClose={onClose}>
      {/* 공통 브랜드 헤더 */}
      <BrandHeader title={page.title} subtitle={page.subtitle} qrUrl={qrUrl} />

      {/* Key info bar */}
      <div className="flex gap-4 mb-5 text-[10px]">
        {page.brand && (
          <div className="flex items-center gap-1">
            <span className="text-[#86868b]">브랜드</span>
            <span className="font-medium text-[#1d1d1f]">{page.brand}</span>
          </div>
        )}
        {page.category && (
          <div className="flex items-center gap-1">
            <span className="text-[#86868b]">카테고리</span>
            <span className="font-medium text-[#1d1d1f]">{page.category}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <span className="text-[#86868b]">제품 수</span>
          <span className="font-medium text-[#1d1d1f]">{items.length}종</span>
        </div>
      </div>

      {/* Feature highlights (compact) */}
      {featureSections.length > 0 && featureSections[0].items && (
        <div className="mb-5">
          <h2 className="text-[13px] font-bold text-[#1d1d1f] mb-2 pb-1 border-b border-gray-200">주요 특징</h2>
          <div className="grid grid-cols-3 gap-3">
            {featureSections[0].items.slice(0, 6).map((ft, i) => (
              <div key={i} className="text-center py-2">
                {ft.highlight_number && <div className="text-[16px] font-bold text-[#1d1d1f]">{ft.highlight_number}</div>}
                <div className="text-[10px] font-medium text-[#1d1d1f]">{ft.title}</div>
                <div className="text-[9px] text-[#86868b] mt-0.5">{ft.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product list table */}
      {items.length > 0 && (
        <div className="mb-5">
          <h2 className="text-[13px] font-bold text-[#1d1d1f] mb-2 pb-1 border-b border-gray-200">제품 목록</h2>
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr className="bg-[#f5f5f7]">
                <th className="text-left px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200 w-8">#</th>
                <th className="text-left px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200">제품명</th>
                <th className="text-left px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200">모델</th>
                {page.brand ? null : <th className="text-left px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200">브랜드</th>}
                <th className="text-center px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200">가격</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className={i % 2 ? "bg-[#fafafa]" : ""}>
                  <td className="px-2 py-1 text-[#86868b] border-b border-gray-100">{i + 1}</td>
                  <td className="px-2 py-1 font-medium text-[#1d1d1f] border-b border-gray-100">{item.product_name}</td>
                  <td className="px-2 py-1 text-[#6e6e73] border-b border-gray-100">{item.model || "-"}</td>
                  {page.brand ? null : <td className="px-2 py-1 text-[#6e6e73] border-b border-gray-100">{item.brand || "-"}</td>}
                  <td className="px-2 py-1 text-center font-medium border-b border-gray-100" style={{ color: accentColor }}>문의</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Spec comparison table */}
      {specSections.map((sec, si) => (
        <PdfSpecTable key={si} sec={sec} />
      ))}

      {/* 공통 브랜드 푸터 */}
      <BrandFooter extraText="본 자료는 제품 정보 제공 목적으로 작성되었습니다. 가격은 별도 문의 바랍니다." />
    </PdfPreviewModal>
  );
}

/* ── Presentation ESC handler ── */
function PresentationEscHandler({ onExit }) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onExit(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onExit]);
  return null;
}


/* ── Section Renderer ────────────────────────────────── */
function SectionRenderer({ section: sec, index, allSections = [], heroImage, productTitle, productCode, showcaseSelections, onShowcaseChange, isRental, canPurchase = false, quantity = 1, onQuantityChange, onInquiry, onBuyNow, ctaLabel }) {
  const isEven = index % 2 === 0;
  const bg = isEven ? "bg-white" : "bg-[#fbfbfd]";
  const accent = isRental ? "rent" : "sale";

  switch (sec.type) {
    case "rich_html":
    case "text":
      return (
        <section className={`${bg} py-16`}>
          <div className="max-w-4xl mx-auto px-6">
            {sec.title && <h2 className="text-2xl md:text-3xl font-bold mb-6 tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>}
            {sec.content && (
              <div className="prose prose-lg max-w-none product-detail-html" style={{ color: "var(--ink-700)" }} dangerouslySetInnerHTML={{ __html: sec.content }} />
            )}
          </div>
        </section>
      );
    case "product_details":
    case "feature_highlight":
      return <FeatureHighlight sec={sec} bg={bg} accent={accent} />;
    case "spec_table":
      return <SpecTable sec={sec} bg={bg} accent={accent} />;
    case "product_card":
      return <ProductCard sec={sec} bg={bg} index={index} heroImage={heroImage} accent={accent} />;
    case "rental_plans":
      return <RentalPlans sec={sec} />;
    case "rental_calculator":
      return <RentalCalculator sec={sec} allSections={allSections} />;
    case "cta":
      return <CTASection sec={sec} isRental={isRental} canPurchase={canPurchase} quantity={quantity} onQuantityChange={onQuantityChange} onInquiry={onInquiry} onBuyNow={onBuyNow} ctaLabel={ctaLabel} />;
    case "image_with_text":
    case "image_text_block":
      return <ImageTextBlock sec={sec} bg={bg} accent={accent} />;
    case "heading_text":
      return <HeadingTextSection sec={sec} bg={bg} accent={accent} />;
    case "product_showcase":
      return <ProductShowcaseSection sec={sec} bg={bg} heroImage={heroImage} productTitle={productTitle} productCode={productCode} onSelectionsChange={onShowcaseChange} isRental={isRental} canPurchase={canPurchase} quantity={quantity} onQuantityChange={onQuantityChange} onInquiry={onInquiry} onBuyNow={onBuyNow} ctaLabel={ctaLabel} />;
    case "product_options":
      return <ProductOptionsSection sec={sec} bg={bg} accent={accent} />;
    case "product_grid":
      return <ProductGridSection sec={sec} bg={bg} accent={accent} />;
    case "sale_pricing":
      return <SalePricingSection sec={sec} bg={bg} accent={accent} />;
    case "rental_pricing":
      return <RentalPricingSection sec={sec} bg={bg} showcaseSelections={showcaseSelections} />;
    case "toner_spec":
      return <TonerSpecSection sec={sec} bg={bg} accent={accent} />;
    default:
      /* 알 수 없는 섹션 — content/items가 있으면 제네릭 렌더링 */
      if (sec.items && sec.items.length > 0) {
        return <FeatureHighlight sec={sec} bg={bg} accent={accent} />;
      }
      if (sec.content) {
        return (
          <section className={`${bg} py-16`}>
            <div className="max-w-4xl mx-auto px-6">
              {sec.title && <h2 className="text-2xl md:text-3xl font-bold mb-6 tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>}
              <div className="prose prose-lg max-w-none product-detail-html" style={{ color: "var(--ink-700)" }} dangerouslySetInnerHTML={{ __html: sec.content }} />
            </div>
          </section>
        );
      }
      return null;
  }
}

/* ── Accent helper: returns rent/sale CSS vars based on accent prop ── */
function accentVars(accent = "sale") {
  const a = accent === "rent" ? "rent" : "sale";
  return {
    primary:    `var(--${a}-500)`,
    primaryHover: `var(--${a}-600)`,
    primaryDark: `var(--${a}-700)`,
    primaryDeep: `var(--${a}-900)`,
    soft:       `var(--${a}-50)`,
    softer:     `var(--${a}-100)`,
    border:     `var(--${a}-200)`,
    light:      `var(--${a}-400)`,
    btn:        a === "rent" ? "btn-rent" : "btn-sale",
    btnGhost:   a === "rent" ? "btn-ghost-rent" : "btn-ghost-sale",
    tag:        a === "rent" ? "tag-rent" : "tag-sale",
  };
}


/* ── Rental Plans — green accent comparison table + tier cards ── */
function RentalPlans({ sec }) {
  const [ref, visible] = useReveal();
  const plans = sec.plans || [];
  const recommended = plans.length >= 2 ? 1 : 0;
  const showDeposit = plans.some((plan) => Number(plan.deposit || 0) > 0);

  return (
    <section ref={ref} className="py-20" style={{ background: "var(--ink-25)" }}>
      <div className="max-w-5xl mx-auto px-6">
        <div className={`text-center mb-12 reveal ${visible ? "visible" : ""}`}>
          <div className="t-eyebrow mb-2.5" style={{ color: "var(--rent-600)" }}>요금제</div>
          {sec.title && (
            <h2 className="t-headline text-[28px] sm:text-[36px] md:text-[40px] mb-3" style={{ fontWeight: 700 }}>
              {sec.title}
            </h2>
          )}
          <p style={{ color: "var(--ink-500)", fontSize: 16 }}>비즈니스 규모에 맞는 최적의 요금제를 선택하세요</p>
        </div>

        {/* ── Comparison table (desktop) ── */}
        {plans.length > 1 && (
          <div className={`hidden md:block overflow-hidden rounded-[20px] bg-white reveal-scale ${visible ? "visible" : ""}`}
               style={{ border: "1px solid var(--line-soft)", boxShadow: "var(--sh-2)", transitionDelay: "0.2s", marginBottom: 48 }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: "var(--ink-50)", borderBottom: "1px solid var(--line-soft)" }}>
                  <th className="py-4 px-6 text-left text-[13px] font-semibold w-[180px]" style={{ color: "var(--ink-400)" }}>요금제</th>
                  {plans.map((plan, i) => (
                    <th key={i} className="py-4 px-4 text-center" style={{ background: i === recommended ? "var(--rent-50)" : undefined }}>
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="text-white text-[10px] font-bold w-6 h-6 rounded-md flex items-center justify-center" style={{ background: "var(--rent-500)" }}>
                          {plan.plan_no || String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-sm font-bold" style={{ color: "var(--ink-900)" }}>{plan.plan_name || plan.model_label}</span>
                        {i === recommended && <span className="tag-pill tag-rent" style={{ fontSize: 9, padding: "2px 8px" }}>추천</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  { lbl: "월 렌탈료", get: (p) => `${(p.monthly_price || 0).toLocaleString()}원`, big: true },
                  showDeposit && { lbl: "보증금", get: (p) => <>{(p.deposit || 0).toLocaleString()}원<span className="block text-[10px]" style={{ color: "var(--ink-400)" }}>인지 반환</span></> },
                  { lbl: "기본 출력매수", get: (p) => p.included_pages || "-" },
                  { lbl: "약정기간", get: (p) => p.contract_period || "3년" },
                  { lbl: "선택사항", get: (p) => p.options || "-" },
                ].filter(Boolean).map((row, ri, arr) => (
                  <tr key={ri} style={{ borderBottom: ri < arr.length - 1 ? "1px solid var(--line-soft)" : "none" }}>
                    <td className="py-3.5 px-6 font-medium" style={{ color: "var(--ink-400)" }}>{row.lbl}</td>
                    {plans.map((plan, i) => (
                      <td
                        key={i}
                        className="py-3.5 px-4 text-center"
                        style={{
                          background: i === recommended ? "var(--rent-50)" : undefined,
                          color: row.big && i === recommended ? "var(--rent-700)" : "var(--ink-900)",
                          fontWeight: row.big ? 800 : 500,
                          fontSize: row.big && i === recommended ? 18 : 14,
                          fontVariantNumeric: row.big ? "tabular-nums" : "normal",
                        }}
                      >
                        {row.get(plan)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Card layout (always shown on mobile, single plan on desktop too) ── */}
        <div className={`${plans.length > 1 ? "md:hidden" : ""} grid gap-5 ${plans.length >= 3 ? "sm:grid-cols-3" : plans.length === 2 ? "sm:grid-cols-2" : ""}`}>
          {plans.map((plan, i) => {
            const isRec = i === recommended;
            return (
              <div key={i}
                className={`rounded-[20px] p-6 transition-all duration-500 reveal ${visible ? "visible" : ""} ${isRec ? "relative" : ""}`}
                style={{
                  border: isRec ? "2px solid var(--rent-500)" : "1.5px solid var(--ink-150)",
                  background: isRec ? "var(--rent-50)" : "white",
                  boxShadow: isRec ? "var(--sh-rent)" : "var(--sh-1)",
                  transitionDelay: `${i * 0.15}s`,
                }}>
                {isRec && plans.length > 1 && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[11px] font-bold px-4 py-1 rounded-full shadow" style={{ background: "var(--rent-500)", letterSpacing: 0 }}>추천</span>
                )}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <span className="text-white text-sm font-bold w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--rent-500)" }}>
                      {plan.plan_no || String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-lg font-bold" style={{ color: "var(--ink-900)" }}>
                      {plan.model_label || plan.plan_name}
                    </span>
                  </div>
                  <span className="num-display text-[20px] sm:text-[24px]" style={{ color: "var(--rent-700)" }}>
                    월{(plan.monthly_price || 0).toLocaleString()}원
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {[
                    ...(Number(plan.deposit || 0) > 0 ? [["보증금", <>{(plan.deposit || 0).toLocaleString()}원<span className="text-xs block" style={{ color: "var(--ink-400)" }}>(인지 반환)</span></>]] : []),
                    ["기본 출력매수", <>{plan.included_pages || "-"}{plan.color_pages && <><br/>{plan.color_pages}</>}</>],
                    ["약정기간", plan.contract_period || "3년"],
                    ["선택사항", plan.options || "-"],
                  ].map(([k, v], ki) => (
                    <div key={ki}>
                      <div className="mb-1" style={{ color: "var(--ink-400)" }}>{k}</div>
                      <div className="font-semibold text-xs leading-relaxed" style={{ color: "var(--ink-900)" }}>{v}</div>
                    </div>
                  ))}
                </div>
                {plan.note && (
                  <p className="mt-3 text-xs" style={{ color: "var(--ink-400)" }}>* {plan.note}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Rental Notice ── */}
        <div className={`mt-12 rounded-[20px] p-6 md:p-8 reveal ${visible ? "visible" : ""}`}
             style={{ background: "var(--rent-50)", border: "1px solid var(--rent-100)", transitionDelay: "0.4s" }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rent-700)" }}>렌탈 안내</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["계약기간", "3년 / 5년 (협의 가능)"],
                  ["보증금", "200,000원 (계약 종료 시 전액 반환)"],
                  ["포함사항", "복합기 설치 · 정기점검 · 토너/드럼 교체 · 소모품 무상 공급"],
                  ["초과 요금", "흑백 장당 15원 / 컬러 장당 80원 (요금제에 따라 상이)"],
                  ["결제방식", "월 자동이체 (세금계산서 발행)"],
                  ["설치지역", "전국 설치 가능 (서울·경기 당일 설치)"],
                ].map(([k, v], i, arr) => (
                  <tr key={i} style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(31,182,131,0.15)" : "none" }}>
                    <td className="py-3 pr-4 font-semibold whitespace-nowrap w-[120px]" style={{ color: "var(--rent-700)" }}>{k}</td>
                    <td className="py-3" style={{ color: "var(--ink-700)" }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs" style={{ color: "var(--ink-400)" }}>* 상기 요금은 VAT 별도이며, 실제 요금은 협의에 따라 달라질 수 있습니다.</p>
        </div>
      </div>
    </section>
  );
}


/* ── Rental Calculator — interactive page cost estimator ── */
function RentalCalculator({ sec, allSections = [] }) {
  const [ref, visible] = useReveal();
  const [bwPages, setBwPages] = React.useState(1000);
  const [colorPages, setColorPages] = React.useState(100);

  // Get plan data from rental_plans section
  const rentalSec = allSections.find(s => s.type === "rental_plans");
  const plans = (rentalSec && rentalSec.plans) || [];
  // Use first plan as default for calculator
  const basePlan = plans[0] || {};
  const extraBwPrice = basePlan.extra_bw_price || 15;
  const extraColorPrice = basePlan.extra_color_price || 80;
  // Parse included pages from text (e.g., "흑백 2,000매...")
  const parseIncluded = (txt) => { const m = String(txt||"").replace(/,/g,"").match(/(\d+)/); return m ? parseInt(m[1]) : 2000; };
  const parseIncColor = (txt) => { const m = String(txt||"").replace(/,/g,"").match(/(\d+)/); return m ? parseInt(m[1]) : 200; };

  return (
    <section ref={ref} className="py-20" style={{ background: "var(--ink-25)" }}>
      <div className="max-w-5xl mx-auto px-6">
        <div className={`text-center mb-10 reveal ${visible ? "visible" : ""}`}>
          <div className="t-eyebrow mb-2.5" style={{ color: "var(--rent-600)" }}>비용 계산기</div>
          {sec.title && (
            <h2 className="t-headline text-[28px] sm:text-[36px] mb-3" style={{ fontWeight: 700 }}>
              {sec.title}
            </h2>
          )}
          {sec.description && (
            <p style={{ color: "var(--ink-500)", fontSize: 15 }}>{sec.description}</p>
          )}
        </div>

        {/* Input */}
        <div className={`bg-white rounded-[20px] p-8 mb-8 reveal ${visible ? "visible" : ""}`}
             style={{ border: "1px solid var(--line-soft)", boxShadow: "var(--sh-1)", transitionDelay: "0.1s" }}>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--ink-500)" }}>월간 흑백 출력매수</label>
              <input type="number" value={bwPages} onChange={e => setBwPages(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full px-4 py-3 rounded-xl text-lg font-semibold focus:outline-none transition"
                style={{ border: "1.5px solid var(--ink-150)", color: "var(--ink-900)" }}
                onFocus={(e) => e.target.style.borderColor = "var(--rent-500)"}
                onBlur={(e) => e.target.style.borderColor = "var(--ink-150)"}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--ink-500)" }}>월간 컬러 출력매수</label>
              <input type="number" value={colorPages} onChange={e => setColorPages(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full px-4 py-3 rounded-xl text-lg font-semibold focus:outline-none transition"
                style={{ border: "1.5px solid var(--ink-150)", color: "var(--ink-900)" }}
                onFocus={(e) => e.target.style.borderColor = "var(--rent-500)"}
                onBlur={(e) => e.target.style.borderColor = "var(--ink-150)"}
              />
            </div>
          </div>
        </div>

        {/* Per-plan cost comparison */}
        {plans.length > 0 ? (
          <div className={`grid gap-4 ${plans.length >= 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : ""} reveal ${visible ? "visible" : ""}`} style={{ transitionDelay: "0.2s" }}>
            {plans.map((plan, i) => {
              const incBw = parseIncluded(plan.included_pages);
              const incColor = parseIncColor(plan.color_pages);
              const eBw = Math.max(0, bwPages - incBw);
              const eColor = Math.max(0, colorPages - incColor);
              const bwExtra = plan.extra_bw_price || extraBwPrice;
              const colorExtra = plan.extra_color_price || extraColorPrice;
              const total = (plan.monthly_price || 0) + (eBw * bwExtra) + (eColor * colorExtra);
              const isMiddle = i === 1;
              return (
                <div key={i} className="bg-white rounded-[20px] p-6 transition"
                     style={{
                       border: isMiddle ? "2px solid var(--rent-500)" : "1.5px solid var(--ink-150)",
                       background: isMiddle ? "var(--rent-50)" : "white",
                       boxShadow: isMiddle ? "var(--sh-rent)" : "var(--sh-1)",
                     }}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-white text-xs font-bold w-6 h-6 rounded flex items-center justify-center" style={{ background: "var(--rent-500)" }}>
                      {plan.plan_no || i+1}
                    </span>
                    <span className="font-bold" style={{ color: "var(--ink-900)" }}>{plan.plan_name || plan.model_label}</span>
                    {isMiddle && <span className="tag-pill tag-rent ml-auto" style={{ fontSize: 10, padding: "2px 8px" }}>표준</span>}
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span style={{ color: "var(--ink-400)" }}>기본 렌탈료</span>
                      <span className="font-semibold tabular-nums" style={{ color: "var(--ink-900)" }}>{(plan.monthly_price||0).toLocaleString()}원</span>
                    </div>
                    {eBw > 0 && (
                      <div className="flex justify-between">
                        <span style={{ color: "var(--ink-400)" }}>흑백 초과 {eBw.toLocaleString()}매</span>
                        <span className="font-semibold tabular-nums" style={{ color: "var(--hot)" }}>+{(eBw*bwExtra).toLocaleString()}원</span>
                      </div>
                    )}
                    {eColor > 0 && (
                      <div className="flex justify-between">
                        <span style={{ color: "var(--ink-400)" }}>컬러 초과 {eColor.toLocaleString()}매</span>
                        <span className="font-semibold tabular-nums" style={{ color: "var(--hot)" }}>+{(eColor*colorExtra).toLocaleString()}원</span>
                      </div>
                    )}
                  </div>
                  <div className="pt-3 text-center" style={{ borderTop: "1px solid var(--line-soft)" }}>
                    <div className="text-xs mb-0.5" style={{ color: "var(--ink-400)" }}>예상 월 비용</div>
                    <div className="num-display text-[24px]" style={{ color: "var(--rent-700)" }}>월 {total.toLocaleString()}원</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`bg-white rounded-[20px] p-6 text-center reveal ${visible ? "visible" : ""}`}
               style={{ border: "1px solid var(--line-soft)" }}>
            <p style={{ color: "var(--ink-400)" }}>요금제 데이터가 없습니다. 렌탈 상담을 통해 견적을 받아보세요.</p>
          </div>
        )}

        {sec.note && (
          <p className="mt-6 text-xs text-center" style={{ color: "var(--ink-400)" }}>* {sec.note}</p>
        )}
      </div>
    </section>
  );
}


/* ── Feature Highlight — with accent-aware icons ── */
function FeatureHighlight({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const items = (sec.items && sec.items.length > 0)
    ? sec.items
    : (sec.features || []).map((f, i) => (typeof f === "string" ? { title: f, description: "", icon: ["shield","speed","palette","gear","zap","target"][i % 6] } : f));
  return (
    <section ref={ref} className={`py-20 ${bg}`}>
      <div className="max-w-[1180px] mx-auto px-6">
        {sec.title && (
          <div className={`mb-12 reveal ${visible ? "visible" : ""}`}>
            <div className="t-eyebrow text-center mb-2.5" style={{ color: a.primaryDark }}>핵심 성능</div>
            <h2 className="t-headline text-[28px] sm:text-[36px] md:text-[40px] text-center" style={{ fontWeight: 700 }}>
              {sec.title}
            </h2>
          </div>
        )}
        <div className={`grid gap-3 sm:gap-4 ${items.length === 4 ? "grid-cols-2 lg:grid-cols-4" : items.length <= 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((item, i) => (
            <FeatureItem key={i} item={item} visible={visible} index={i} accent={accent} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureItem({ item, visible, index, accent = "sale" }) {
  const displayVal = useCountUp(item.highlight_number, visible);
  const a = accentVars(accent);
  return (
    <div
      className={`card p-6 sm:p-7 group reveal ${visible ? "visible" : ""}`}
      style={{
        background: "white",
        color: "inherit",
        border: "1px solid var(--line-soft)",
        ...stagger(index + 1, 0.1),
      }}
    >
      <div
        className="w-11 h-11 rounded-xl grid place-items-center mb-5 transition-transform duration-500 group-hover:scale-110"
        style={{
          background: a.softer,
          color: a.primaryDark,
        }}
      >
        <SvgIcon name={item.icon} size={22} className="transition-transform" />
      </div>
      {item.highlight_number && (
        <div
          className="num-display mb-1.5"
          style={{ color: "var(--ink-900)", fontSize: "clamp(28px, 4vw, 40px)" }}
        >
          {displayVal}
        </div>
      )}
      <h3 className="text-[15px] font-bold mb-1.5">{item.title}</h3>
      <p className="text-[13px] leading-relaxed" style={{ color: "var(--ink-400)" }}>
        {item.description}
      </p>
    </div>
  );
}


function specCellValue(cell) {
  if (React.isValidElement(cell)) return cell;
  if (cell == null || cell === "") return "-";
  if (typeof cell === "object") return cell.value || cell.text || cell.label || JSON.stringify(cell);
  return cell;
}

function normalizeSpecTable(sec = {}) {
  const fallbackHeaders = ["항목", "내용"];
  const rawHeaders = Array.isArray(sec.headers) && sec.headers.length
    ? sec.headers
    : Array.isArray(sec.columns) && sec.columns.length
      ? sec.columns
      : fallbackHeaders;

  let rows = [];
  if (Array.isArray(sec.rows) && sec.rows.length) {
    rows = sec.rows.map(row => {
      if (Array.isArray(row)) return row;
      if (row && typeof row === "object") {
        return rawHeaders.map((h, idx) => row[h] ?? row[String(h).toLowerCase()] ?? Object.values(row)[idx] ?? "");
      }
      return [row];
    });
  } else if (Array.isArray(sec.specs) && sec.specs.length) {
    rows = sec.specs.map(item => {
      if (Array.isArray(item)) return item;
      if (item && typeof item === "object") {
        return [
          item.항목 ?? item.item ?? item.key ?? item.label ?? item.name ?? item.title ?? "",
          item.내용 ?? item.value ?? item.description ?? item.detail ?? item.text ?? "",
        ];
      }
      return [item];
    });
  } else if (sec.specs && typeof sec.specs === "object") {
    rows = Object.entries(sec.specs);
  }

  const colCount = Math.max(rawHeaders.length, ...rows.map(row => row.length), fallbackHeaders.length);
  const headers = [...rawHeaders];
  while (headers.length < colCount) headers.push(headers.length === 0 ? "항목" : "내용");
  const normalizedRows = rows.map(row => {
    const next = [...row];
    while (next.length < colCount) next.push("");
    return next;
  });

  return { headers, rows: normalizedRows };
}

function PdfSpecTable({ sec }) {
  const { headers, rows } = normalizeSpecTable(sec);
  if (!rows.length) return null;
  return (
    <div className="mb-5">
      <h2 className="text-[13px] font-bold text-[#1d1d1f] mb-2 pb-1 border-b border-gray-200">{sec.title || "사양 비교"}</h2>
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-[#f5f5f7]">
            {headers.map((h, j) => (
              <th key={j} className={`px-2 py-1.5 font-semibold text-[#1d1d1f] border-b border-gray-200 ${j === 0 ? "text-left" : "text-center"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, j) => (
            <tr key={j} className={j % 2 ? "bg-[#fafafa]" : ""}>
              {row.map((cell, k) => (
                <td key={k} className={`px-2 py-1 border-b border-gray-100 ${k === 0 ? "text-left font-medium text-[#6e6e73]" : "text-center text-[#1d1d1f]"}`}>
                  {specCellValue(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Spec Table — accent-aware header with row stagger ── */
function SpecTable({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const { headers, rows } = normalizeSpecTable(sec);
  if (!rows.length) return null;
  return (
    <section ref={ref} className={`py-20 ${bg}`}>
      <div className="max-w-5xl mx-auto px-6">
        {sec.title && (
          <div className={`mb-10 text-center reveal ${visible ? "visible" : ""}`}>
            <div className="t-eyebrow mb-2.5" style={{ color: a.primaryDark }}>전체 사양</div>
            <h2 className="t-headline text-[28px] sm:text-[36px]" style={{ fontWeight: 700 }}>
              {sec.title}
            </h2>
          </div>
        )}
        <div
          className={`overflow-x-auto rounded-[20px] bg-white reveal-scale ${visible ? "visible" : ""}`}
          style={{ border: "1px solid var(--line-soft)", boxShadow: "var(--sh-1)", transitionDelay: "0.2s" }}
        >
          <table className="w-full">
            <thead>
              <tr style={{ background: a.soft, borderBottom: `2px solid ${a.primary}` }}>
                {headers.map((h, j) => (
                  <th
                    key={j}
                    className="py-4 px-6 text-[13px] font-bold"
                    style={{
                      textAlign: j === 0 ? "left" : "center",
                      color: a.primaryDark,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, j) => (
                <tr
                  key={j}
                  className={`transition-all duration-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}
                  style={{
                    borderBottom: j < (rows.length - 1) ? "1px solid var(--line-soft)" : "none",
                    transitionDelay: `${0.3 + j * 0.05}s`,
                    transitionProperty: "opacity, transform",
                  }}
                >
                  {row.map((cell, k) => (
                    <td
                      key={k}
                      className="py-4 px-6 text-[14px] font-medium"
                      style={{
                        textAlign: k === 0 ? "left" : "center",
                        color: k === 0 ? "var(--ink-400)" : "var(--ink-900)",
                      }}
                    >
                      {specCellValue(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}


/* ── Product Card — with alternating slide direction (accent-aware) ── */
function ProductCard({ sec, bg, index, heroImage, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const reverse = index % 2 !== 0;
  return (
    <section ref={ref} className={`py-20 ${bg} overflow-hidden`}>
      <div className="max-w-5xl mx-auto px-6">
        <div className={`flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-12 md:gap-16`}>
          {/* Text Content */}
          <div className={`flex-1 text-center md:text-left ${visible ? "opacity-100 translate-x-0" : `opacity-0 ${reverse ? "translate-x-12" : "-translate-x-12"}`} transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]`}>
            <h2 className="font-bold mb-4 tracking-tight leading-tight" style={{ color: "var(--ink-900)", fontSize: "clamp(28px, 4.5vw, 48px)", letterSpacing: "-0.025em" }}>
              {sec.product_name}
            </h2>
            {sec.tagline && (
              <p className="text-lg md:text-xl mb-8 leading-relaxed font-light" style={{ color: "var(--ink-500)" }}>{sec.tagline}</p>
            )}
            {sec.highlight_spec && (
              <div className="mb-8 flex items-baseline gap-2 justify-center md:justify-start">
                <span className="num-display" style={{ color: a.primaryDark, fontSize: "clamp(40px, 6vw, 64px)" }}>{sec.highlight_spec.value}</span>
                <span className="text-lg" style={{ color: "var(--ink-500)" }}>{sec.highlight_spec.label}</span>
              </div>
            )}
            {sec.features && (
              <ul className="space-y-4 mb-8">
                {sec.features.map((f, j) => (
                  <li key={j}
                    className={`flex items-start gap-3 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"} transition-all duration-500`}
                    style={{ transitionDelay: `${0.4 + j * 0.1}s` }}>
                    <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full grid place-items-center" style={{ background: a.softer, color: a.primaryDark }}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <span className="text-[15px] leading-relaxed" style={{ color: "var(--ink-700)" }}>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            {(sec.price_label || sec.price) && (
              <div className="inline-flex items-center gap-1 text-lg font-bold" style={{ color: a.primaryDark }}>
                {sec.price_label || (sec.price ? `${Number(sec.price).toLocaleString()}원` : "")}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
              </div>
            )}
          </div>

          {/* Product Visual */}
          <div className={`flex-1 flex justify-center ${visible ? "opacity-100 scale-100" : "opacity-0 scale-90"} transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]`}
               style={{ transitionDelay: "0.15s" }}>
            {(sec.image_url || heroImage) ? (
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-[40px] blur-2xl scale-105"
                  style={{
                    background: accent === "rent"
                      ? "linear-gradient(135deg, rgba(31,182,131,0.15), rgba(110,59,255,0.10))"
                      : "linear-gradient(135deg, rgba(10,107,255,0.15), rgba(110,59,255,0.10))",
                  }}
                />
                <img src={sec.image_url || heroImage} alt={sec.product_name}
                  className="relative w-80 h-80 object-contain drop-shadow-xl hover:scale-105 transition-transform duration-500" />
              </div>
            ) : (
              <div className="w-72 h-72 rounded-[40px] flex items-center justify-center relative overflow-hidden group" style={{ background: a.soft, border: `1px solid ${a.softer}` }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                <SvgIcon name="printer" size={96} style={{ color: a.border }} className="transition-transform duration-300 group-hover:scale-110" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


/* ── LoopVideo: handles loop/once/N-times playback with transitions ── */
function LoopVideo({ src, className, loop = "loop", transition = "cut" }) {
  const ref = React.useRef(null);
  const overlayRef = React.useRef(null);
  const countRef = React.useRef(0);
  const maxPlays = loop === "loop" ? Infinity : loop === "once" ? 1 : parseInt(loop, 10) || Infinity;
  const isInfinite = maxPlays === Infinity;
  React.useEffect(() => { countRef.current = 0; }, [src, loop]);
  const doTransition = React.useCallback(() => {
    const el = overlayRef.current;
    if (!el) return;
    if (transition === "fade-black") { el.style.opacity = "1"; el.style.background = "#000"; setTimeout(() => { el.style.opacity = "0"; }, 300); }
    else if (transition === "fade-white") { el.style.opacity = "1"; el.style.background = "#fff"; setTimeout(() => { el.style.opacity = "0"; }, 300); }
    else if (transition === "crossfade") { if (ref.current) { ref.current.style.opacity = "0"; } setTimeout(() => { if (ref.current) ref.current.style.opacity = "1"; }, 50); }
  }, [transition]);
  const onEnded = React.useCallback(() => {
    countRef.current += 1;
    if (countRef.current < maxPlays && ref.current) {
      doTransition();
      ref.current.currentTime = 0;
      ref.current.play().catch(() => {});
    }
  }, [maxPlays, doTransition]);
  const needsOverlay = transition && transition !== "cut";
  return (
    <div className="relative inline-block">
      <video ref={ref} src={src} className={`${className} transition-opacity duration-300`} muted autoPlay playsInline loop={isInfinite && transition === "cut"} onEnded={isInfinite && transition === "cut" ? undefined : onEnded} />
      {needsOverlay && <div ref={overlayRef} className="absolute inset-0 pointer-events-none opacity-0 rounded-[inherit]" style={{ transition: "opacity 0.3s ease-in-out" }} />}
    </div>
  );
}

/* ── Font size map ── */
const FONT_SIZE_MAP = {
  sm: { title: "text-lg", desc: "text-xs" },
  md: { title: "text-xl", desc: "text-sm" },
  lg: { title: "text-2xl md:text-3xl", desc: "text-base" },
  xl: { title: "text-3xl md:text-4xl", desc: "text-lg" },
  "2xl": { title: "text-4xl md:text-5xl", desc: "text-xl" },
};

/* ── Image + Text Block — accent-aware feature list + CTA ── */
function ImageTextBlock({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const pos = sec.image_position || "right";
  const fs = FONT_SIZE_MAP[sec.font_size] || FONT_SIZE_MAP.lg;
  const mediaEl = sec.video_url ? (
    <div className="flex-1 flex justify-center">
      <LoopVideo src={sec.video_url} className="max-h-80 rounded-[20px] object-cover" loop={sec.video_loop || "loop"} transition={sec.video_transition || "cut"} />
    </div>
  ) : sec.image_url ? (
    <div className="flex-1 flex justify-center">
      <img src={sec.image_url} alt={sec.title} className="max-h-80 object-contain rounded-[20px]"
        style={{ boxShadow: "var(--sh-3)" }}
        loading="lazy" onError={e => { e.target.style.display = "none"; }} />
    </div>
  ) : null;

  const textEl = (
    <div className="flex-1 space-y-4">
      {sec.title && <h2 className={`${fs.title} font-bold tracking-tight`} style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>}
      {(sec.description || sec.content) && (
        sec.content && sec.content.startsWith("<")
          ? <div className={`${fs.desc} leading-relaxed prose max-w-none`} style={{ color: "var(--ink-500)" }} dangerouslySetInnerHTML={{ __html: sec.content }} />
          : <p className={`${fs.desc} leading-relaxed`} style={{ color: "var(--ink-500)" }}>{sec.description || sec.content}</p>
      )}
      {sec.features && sec.features.length > 0 && (
        <ul className="space-y-2 mt-3">
          {sec.features.map((f, j) => (
            <li key={j} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--ink-700)" }}>
              <svg className="w-5 h-5 mt-0.5 shrink-0" style={{ color: a.primary }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
              {f}
            </li>
          ))}
        </ul>
      )}
      {sec.cta_text && (
        <div className="mt-4">
          <a href={sec.cta_link || "#"} className="inline-flex items-center gap-1 font-semibold hover:underline" style={{ color: a.primaryDark }}>
            {sec.cta_text} <span className="text-lg">›</span>
          </a>
        </div>
      )}
    </div>
  );

  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-5xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {pos === "top" ? (
          <div className="space-y-8 text-center">{mediaEl}{textEl}</div>
        ) : (
          <div className={`flex flex-col md:flex-row items-center gap-10 ${pos === "left" ? "" : "md:flex-row-reverse"}`}>
            {mediaEl}{textEl}
          </div>
        )}
      </div>
    </section>
  );
}


/* ── Heading + Text Section (accent-aware subheading) ── */
function HeadingTextSection({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const bgMap = { white: "bg-white", gray: "bg-[#fbfbfd]", dark: "bg-[#1d1d1f]" };
  const isDark = sec.bg_style === "dark";
  const align = sec.align === "left" ? "text-left" : "text-center";
  return (
    <section ref={ref} className={`${bgMap[sec.bg_style] || bg} py-20`}>
      <div className={`max-w-4xl mx-auto px-6 ${align} transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {sec.heading && (
          <h2
            className={`font-bold tracking-tight ${isDark ? "text-white" : ""}`}
            style={{
              color: isDark ? "white" : "var(--ink-900)",
              fontSize: "clamp(24px, 3.6vw, 36px)",
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
            }}
          >
            {sec.heading}
          </h2>
        )}
        {sec.subheading && (
          <p
            className="mt-3 text-lg font-semibold"
            style={{ color: isDark ? "rgba(255,255,255,0.85)" : a.primaryDark }}
          >
            {sec.subheading}
          </p>
        )}
        {sec.description && (
          <p
            className={`mt-4 text-base leading-relaxed ${sec.align !== "left" ? "max-w-2xl mx-auto" : ""}`}
            style={{ color: isDark ? "rgba(255,255,255,0.6)" : "var(--ink-500)" }}
          >
            {sec.description}
          </p>
        )}
      </div>
    </section>
  );
}

/* ── Product Showcase Section (프리미엄 2단: 이미지+옵션) ── */
function ProductShowcaseSection({ sec, bg, heroImage, productTitle, productCode, onSelectionsChange, isRental, canPurchase = false, quantity = 1, onQuantityChange, onInquiry, onBuyNow, ctaLabel }) {
  const [ref, visible] = useReveal();
  const _secImgs = (sec.images || []).filter(u => u && u.trim());
  const images = _secImgs.length > 0 ? _secImgs : (heroImage ? [heroImage] : []);
  const groups = splitCombinedOptionGroups(sec.option_groups || []);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedOpts, setSelectedOpts] = useState({});

  /* 옵션 변경 시 부모에게 알림 (렌탈표 연동용) */
  const handleSelectOpt = (gi, oi) => {
    const next = { ...selectedOpts, [gi]: oi };
    setSelectedOpts(next);
    if (onSelectionsChange) onSelectionsChange({ opts: next, groups });
  };

  /* ── 엑셀 변수 방식 가격 계산: 선택된 옵션의 unit_price 기반 ── */
  const calcPrice = () => {
    let total = 0;
    let hasPrice = false;
    groups.forEach((group, gi) => {
      const oi = selectedOpts[gi];
      if (oi == null) return;
      const opt = (group.options || [])[oi];
      if (!opt) return;
      const amount = getOptionPriceAmount(group, opt);
      if (amount > 0) {
        total += amount;
        hasPrice = true;
      }
    });
    return hasPrice ? total : null;
  };

  const totalPrice = calcPrice();

  /* ── 견적문의에 보낼 선택 옵션 요약 ── */
  const buildSelections = () => {
    const lines = [];
    groups.forEach((group, gi) => {
      const oi = selectedOpts[gi];
      if (oi == null) return;
      const opt = (group.options || [])[oi];
      if (opt) lines.push(`${group.group_name}: ${opt.name}`);
    });
    if (totalPrice != null) lines.push(`예상 월 비용: ${totalPrice.toLocaleString()}원`);
    return lines;
  };

  const a = accentVars(isRental ? "rent" : "sale");

  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-5xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {(sec.title || sec.price_label) && (
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>
            {sec.price_label && <span className="text-lg" style={{ color: "var(--ink-400)" }}>{sec.price_label}</span>}
          </div>
        )}
        {(sec.sub_info || []).length > 0 && (
          <div className="flex items-center justify-end gap-4 mb-6">
            {sec.sub_info.map((info, si) => (
              <span key={si} className="text-xs flex items-center gap-1.5" style={{ color: "var(--ink-400)" }}>
                {si === 0 ? (
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="1" y="6" width="14" height="11" rx="1"/><path d="M15 9h4l3 4v4h-7"/><circle cx="6" cy="20" r="2"/><circle cx="18" cy="20" r="2"/></svg>
                ) : (
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2h-4a2 2 0 01-2-2v-6a2 2 0 00-2-2h-2a2 2 0 00-2 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/></svg>
                )}
                {info}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-8">
          {/* LEFT: Image */}
          <div className="flex-[3] min-w-0">
            <div
              className="rounded-[20px] aspect-square flex items-center justify-center overflow-hidden relative"
              style={{ background: a.soft, border: `1px solid var(--line-soft)` }}
            >
              {images.length > 0 ? (
                <img src={images[activeImg] || images[0]} alt="" className="max-w-full max-h-full object-contain p-6" />
              ) : (
                <span className="text-sm" style={{ color: "var(--ink-300)" }}>이미지 없음</span>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 backdrop-blur rounded-full flex items-center justify-center transition"
                    style={{ background: "rgba(255,255,255,0.85)", color: "var(--ink-700)", boxShadow: "var(--sh-1)" }}>
                    ‹
                  </button>
                  <button onClick={() => setActiveImg((activeImg + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 backdrop-blur rounded-full flex items-center justify-center transition"
                    style={{ background: "rgba(255,255,255,0.85)", color: "var(--ink-700)", boxShadow: "var(--sh-1)" }}>
                    ›
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-4">
                {images.map((_, ii) => (
                  <button key={ii} onClick={() => setActiveImg(ii)}
                    className="w-2.5 h-2.5 rounded-full transition"
                    style={{ background: ii === activeImg ? a.primary : "var(--ink-200)" }} />
                ))}
              </div>
            )}
          </div>
          {/* RIGHT: Options */}
          <div className="flex-[2] min-w-0 space-y-6">
            {groups.map((group, gi) => (
              <div key={gi}>
                <h3 className="text-base font-bold mb-1" style={{ color: "var(--ink-900)" }}>
                  {group.group_name}. <span className="font-normal text-sm" style={{ color: "var(--ink-400)" }}>
                    {group.group_description || `원하는 ${group.group_name}을 선택하세요.`}
                  </span>
                </h3>
                {/* swatch */}
                {group.group_type === "swatch" && (
                  <div className="flex gap-3 mt-3">
                    {(group.options || []).map((opt, oi) => (
                      <button key={oi} onClick={() => handleSelectOpt(gi, oi)} className="flex flex-col items-center gap-1">
                        <div
                          className="w-8 h-8 rounded-full border-2 transition"
                          style={{
                            borderColor: selectedOpts[gi] === oi ? a.primary : "var(--ink-200)",
                            backgroundColor: opt.color_hex || "#ccc",
                            boxShadow: selectedOpts[gi] === oi ? `0 0 0 2px ${a.softer}` : "none",
                          }}
                        />
                        <span className="text-[10px]" style={{ color: "var(--ink-400)" }}>{opt.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {/* list */}
                {group.group_type === "list" && (
                  <div className="space-y-2 mt-3">
                    {(group.options || []).map((opt, oi) => (
                      <div key={oi} onClick={() => handleSelectOpt(gi, oi)}
                        className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition"
                        style={{
                          border: selectedOpts[gi] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                          background: selectedOpts[gi] === oi ? a.soft : "white",
                        }}>
                        <span className="text-sm font-semibold" style={{ color: selectedOpts[gi] === oi ? a.primaryDark : "var(--ink-900)" }}>{opt.name}</span>
                        {opt.price_label && <span className="text-xs" style={{ color: "var(--ink-400)" }}>{opt.price_label}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {/* button */}
                {group.group_type === "button" && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(group.options || []).map((opt, oi) => (
                      <button key={oi} onClick={() => handleSelectOpt(gi, oi)}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                        style={{
                          border: selectedOpts[gi] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                          background: selectedOpts[gi] === oi ? a.primary : "white",
                          color: selectedOpts[gi] === oi ? "white" : "var(--ink-900)",
                        }}>
                        {opt.name}
                      </button>
                    ))}
                  </div>
                )}
                {/* card */}
                {group.group_type === "card" && (
                  <div className="space-y-2 mt-3">
                    {(group.options || []).map((opt, oi) => (
                      <div key={oi} onClick={() => handleSelectOpt(gi, oi)}
                        className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition"
                        style={{
                          border: selectedOpts[gi] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                          background: selectedOpts[gi] === oi ? a.soft : "white",
                        }}>
                        {opt.image_url && <img src={opt.image_url} alt="" className="w-12 h-12 object-contain shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold" style={{ color: selectedOpts[gi] === oi ? a.primaryDark : "var(--ink-900)" }}>{opt.name || "옵션"}</div>
                          {opt.description && <div className="text-xs mt-0.5" style={{ color: "var(--ink-400)" }}>{opt.description}</div>}
                        </div>
                        {opt.price_label && <span className="text-sm font-bold shrink-0" style={{ color: "var(--ink-900)" }}>{opt.price_label}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {/* CTA + Price display */}
            <div className="pt-4 mt-4 space-y-4" style={{ borderTop: "1px solid var(--line-soft)" }}>
              {totalPrice != null && (
                <div className="rounded-[16px] p-4 text-center" style={{ background: "var(--ink-1000)", color: "white" }}>
                  <div className="flex items-center justify-center gap-2 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full detail-pulse" style={{ background: a.primary }} />
                    <p className="text-[11px] font-bold tracking-widest" style={{ color: a.light, letterSpacing: "0.16em" }}>실시간 견적</p>
                  </div>
                  <p className="num-display text-[24px] sm:text-[32px]">
                    {totalPrice.toLocaleString()}<span className="text-base font-semibold ml-1" style={{ color: "rgba(255,255,255,0.5)" }}>원</span>
                  </p>
                  <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>VAT 별도 · 옵션 선택에 따라 변동</p>
                </div>
              )}
              {canPurchase && onQuantityChange && (
                <div className="flex justify-center">
                  <QuantityInput value={quantity} onChange={onQuantityChange} />
                </div>
              )}
              <button
                onClick={isRental && onInquiry ? onInquiry : (onBuyNow || (() => window.scrollTo({ top: 0, behavior: 'smooth' })))}
                className={`${a.btn} w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-[15px] font-semibold transition`}>
                {isRental ? (
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>
                ) : (
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6"/></svg>
                )}
                {ctaLabel || (isRental ? '견적 문의' : '구매하기')}
              </button>
              {productCode && (
                <div className="w-full flex justify-center mt-2">
                  <span className="tag-pill tag-warn inline-flex items-center gap-1.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>
                    상품코드: {productCode}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Product Options Section (accent-aware option groups) ── */
function ProductOptionsSection({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const [selected, setSelected] = useState({});
  const groups = splitCombinedOptionGroups(sec.option_groups || []);
  const a = accentVars(accent);

  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-3xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {sec.title && <h2 className="text-2xl md:text-3xl font-bold mb-8 tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>}
        <div className="space-y-8">
          {groups.map((group, gi) => (
            <div key={gi}>
              <h3 className="text-base font-bold mb-1" style={{ color: "var(--ink-900)" }}>
                {group.group_name}. <span className="font-normal text-sm" style={{ color: "var(--ink-400)" }}>
                  {group.group_type === "checkbox" ? "원하는 항목을 선택하세요." : `원하는 ${group.group_name}을 선택하세요.`}
                </span>
              </h3>
              {group.group_type === "card" && (
                <div className="space-y-2 mt-3">
                  {(group.options || []).map((opt, oi) => (
                    <div key={oi} onClick={() => setSelected(p => ({ ...p, [gi]: oi }))}
                      className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition"
                      style={{
                        border: selected[gi] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                        background: selected[gi] === oi ? a.soft : "white",
                      }}>
                      {opt.image_url && <img src={opt.image_url} alt="" className="w-12 h-12 object-contain shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold" style={{ color: selected[gi] === oi ? a.primaryDark : "var(--ink-900)" }}>{opt.name || "옵션"}</div>
                        {opt.description && <div className="text-xs mt-0.5" style={{ color: "var(--ink-400)" }}>{opt.description}</div>}
                      </div>
                      {opt.price_modifier !== 0 && (
                        <div className="text-sm font-bold shrink-0 tabular-nums" style={{ color: "var(--ink-900)" }}>
                          {opt.price_modifier > 0 ? "+" : ""}{(opt.price_modifier || 0).toLocaleString()}원
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {group.group_type === "button" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {(group.options || []).map((opt, oi) => (
                    <button key={oi} onClick={() => setSelected(p => ({ ...p, [gi]: oi }))}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                      style={{
                        border: selected[gi] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                        background: selected[gi] === oi ? a.primary : "white",
                        color: selected[gi] === oi ? "white" : "var(--ink-900)",
                      }}>
                      {opt.name}
                      {opt.price_modifier !== 0 && <span className="ml-1 text-xs opacity-80 tabular-nums">({opt.price_modifier > 0 ? "+" : ""}{(opt.price_modifier || 0).toLocaleString()})</span>}
                    </button>
                  ))}
                </div>
              )}
              {group.group_type === "color" && (
                <div className="flex gap-3 mt-3">
                  {(group.options || []).map((opt, oi) => (
                    <button key={oi} onClick={() => setSelected(p => ({ ...p, [gi]: oi }))} className="flex flex-col items-center gap-1">
                      <div
                        className="w-8 h-8 rounded-full border-2 transition"
                        style={{
                          borderColor: selected[gi] === oi ? a.primary : "var(--ink-200)",
                          backgroundColor: opt.color_code || "#666",
                          boxShadow: selected[gi] === oi ? `0 0 0 2px ${a.softer}` : "none",
                        }} />
                      <span className="text-[10px]" style={{ color: "var(--ink-400)" }}>{opt.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {group.group_type === "checkbox" && (
                <div className="space-y-2 mt-3">
                  {(group.options || []).map((opt, oi) => (
                    <label key={oi} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition"
                      style={{ border: "1.5px solid var(--ink-150)" }}>
                      <input type="checkbox" className="w-4 h-4 rounded" style={{ accentColor: a.primary }} />
                      <div className="flex-1">
                        <div className="text-sm font-semibold" style={{ color: "var(--ink-900)" }}>{opt.name}</div>
                        {opt.description && <div className="text-xs" style={{ color: "var(--ink-400)" }}>{opt.description}</div>}
                      </div>
                      {opt.price_modifier !== 0 && <span className="text-sm font-bold tabular-nums" style={{ color: "var(--ink-900)" }}>+{(opt.price_modifier || 0).toLocaleString()}원</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Product Grid Section (accent-aware buttons) ── */
function ProductGridSection({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const GRID_ZONES = { "1x1": { cols: 1 }, "1x2": { cols: 1 }, "2x1": { cols: 2 }, "2x2": { cols: 2 }, "3x1": { cols: 3 }, "1x3": { cols: 1 } };
  const { cols = 2 } = GRID_ZONES[sec.gridLayout || "2x1"] || {};
  const cells = sec.cells || [];

  const BUTTON_PRESETS = {
    purchase: { bg: "var(--sale-500)", color: "white" },
    phone:    { bg: "var(--ink-1000)", color: "white" },
    kakao:    { bg: "#fee500", color: "var(--ink-900)" },
    inquiry:  { bg: a.primary, color: "white" },
    cart:     { bg: "var(--rent-500)", color: "white" },
    compare:  { bg: "var(--ink-50)", color: "var(--ink-900)" },
    quote:    { bg: "var(--plum)", color: "white" },
    download: { bg: "var(--ink-50)", color: "var(--ink-700)" },
  };

  const renderBlock = (block, bi) => {
    switch (block.type) {
      case "image": {
        const imgs = block.images || (block.src ? [{ src: block.src }] : []);
        if (imgs.length === 0) return null;
        return (
          <div key={bi} style={{ zIndex: block.zIndex || 0, width: `${block.widthPct || 100}%`, margin: block.align === "center" ? "0 auto" : block.align === "right" ? "0 0 0 auto" : undefined }}>
            <img src={imgs[0]?.src} alt="" className="w-full object-contain rounded-[16px]" loading="lazy" onError={e => { e.target.style.display = "none"; }} />
          </div>
        );
      }
      case "title": return <h2 key={bi} className={`text-xl font-bold ${block.align === "center" ? "text-center" : ""}`} style={{ color: "var(--ink-900)" }}>{block.content}</h2>;
      case "text": return <p key={bi} className={`text-sm leading-relaxed ${block.align === "center" ? "text-center" : ""}`} style={{ color: "var(--ink-500)" }}>{block.content}</p>;
      case "price": return (
        <div key={bi} className={block.priceStyle === "large" ? "text-center" : ""}>
          {block.priceLabel && <span className="text-xs" style={{ color: "var(--ink-400)" }}>{block.priceLabel}</span>}
          <p className={`num-display ${block.priceStyle === "large" ? "text-2xl" : "text-lg"}`} style={{ color: a.primaryDark }}>{(block.amount || 0).toLocaleString()}원</p>
        </div>
      );
      case "buttons": return (
        <div key={bi} className={block.layout === "row" ? "flex gap-2" : block.layout === "grid2" ? "grid grid-cols-2 gap-2" : "space-y-2"}>
          {(block.buttons || []).map((btn, bii) => {
            const preset = BUTTON_PRESETS[btn.action] || { bg: "var(--ink-50)", color: "var(--ink-900)" };
            return (
              <button key={bii}
                className="px-4 py-3 rounded-full text-sm font-bold w-full transition"
                style={{ background: preset.bg, color: preset.color }}>
                {btn.label}
              </button>
            );
          })}
        </div>
      );
      case "options": return <GridOptionsPreview key={bi} categories={block.categories || []} accent={accent} />;
      case "spacer": return <div key={bi} style={{ height: block.height || 24 }} />;
      default: return null;
    }
  };

  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-5xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid gap-8" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {cells.map((cell, ci) => (
            <div key={ci}>
              {(cell.blocks || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((block, bi) => (
                <div key={bi} className="mb-4">{renderBlock(block, bi)}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Grid Options Preview (accent-aware) ── */
function GridOptionsPreview({ categories, accent = "sale" }) {
  const [selected, setSelected] = useState({});
  const a = accentVars(accent);
  return (
    <div className="space-y-4">
      {categories.map((cat, ci) => (
        <div key={ci}>
          <label className="text-xs font-bold mb-1 block" style={{ color: a.primaryDark }}>{cat.title}</label>
          {(cat.displayType === "dropdown" || !cat.displayType) && (
            <select value={selected[ci] ?? ""} onChange={e => setSelected(p => ({ ...p, [ci]: e.target.value }))}
              className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none transition"
              style={{ border: "1.5px solid var(--ink-150)" }}
              onFocus={(e) => e.target.style.borderColor = a.primary}
              onBlur={(e) => e.target.style.borderColor = "var(--ink-150)"}>
              <option value="">선택</option>
              {(cat.options || []).map((opt, oi) => (
                <option key={oi} value={oi}>{opt.name}{opt.price ? ` (+${opt.price.toLocaleString()}원)` : ""}</option>
              ))}
            </select>
          )}
          {cat.displayType === "button" && (
            <div className="flex flex-wrap gap-2">
              {(cat.options || []).map((opt, oi) => (
                <button key={oi} onClick={() => setSelected(p => ({ ...p, [ci]: p[ci] === oi ? null : oi }))}
                  className="px-3 py-1.5 rounded-xl text-sm font-semibold transition"
                  style={{
                    border: selected[ci] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                    background: selected[ci] === oi ? a.soft : "white",
                    color: selected[ci] === oi ? a.primaryDark : "var(--ink-900)",
                  }}>
                  {opt.name}{opt.price > 0 && <span className="text-[10px] ml-1 opacity-60 tabular-nums">+{opt.price.toLocaleString()}</span>}
                </button>
              ))}
            </div>
          )}
          {cat.displayType === "card" && (
            <div className="space-y-1">
              {(cat.options || []).map((opt, oi) => (
                <div key={oi} onClick={() => setSelected(p => ({ ...p, [ci]: p[ci] === oi ? null : oi }))}
                  className="flex items-center justify-between p-3 rounded-xl cursor-pointer transition"
                  style={{
                    border: selected[ci] === oi ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                    background: selected[ci] === oi ? a.soft : "white",
                  }}>
                  <span className="text-sm font-semibold" style={{ color: selected[ci] === oi ? a.primaryDark : "var(--ink-900)" }}>{opt.name}</span>
                  {opt.price > 0 && <span className="text-sm font-bold tabular-nums" style={{ color: "var(--ink-900)" }}>+{opt.price.toLocaleString()}원</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Sale Pricing Section (accent-aware plans + products) ── */
function SalePricingSection({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  const plans = sec.plans || [];
  const products = sec.products || [];
  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-4xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {sec.title && (
          <div className="mb-6">
            <div className="t-eyebrow mb-2.5" style={{ color: a.primaryDark }}>{accent === "rent" ? "월 렌탈료" : "구매 가격"}</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>
          </div>
        )}
        {sec.description && <p className="text-[15px] mb-8" style={{ color: "var(--ink-500)" }}>{sec.description}</p>}
        {/* Plans format */}
        {plans.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            {plans.map((plan, j) => (
              <div key={j} className="rounded-[20px] p-6 transition"
                   style={{
                     border: plan.is_highlighted ? `2px solid ${a.primary}` : "1.5px solid var(--ink-150)",
                     background: plan.is_highlighted ? a.soft : "white",
                     boxShadow: plan.is_highlighted ? (accent === "rent" ? "var(--sh-rent)" : "var(--sh-sale)") : "var(--sh-1)",
                   }}>
                {plan.is_highlighted && <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: a.primaryDark, letterSpacing: "0.16em" }}>추천</span>}
                <h3 className="text-lg font-bold mt-1" style={{ color: "var(--ink-900)" }}>{plan.name}</h3>
                <p className="num-display text-[28px] mt-2" style={{ color: a.primaryDark }}>{plan.price}</p>
                {plan.period && <p className="text-xs mb-4" style={{ color: "var(--ink-400)" }}>{plan.period}</p>}
                {(plan.features || []).length > 0 && (
                  <ul className="space-y-2 mt-4">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: "var(--ink-700)" }}>
                        <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: a.primary }} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Products format (legacy) */}
        {products.length > 0 && (
          <div className="space-y-3">
            {products.map((p, j) => (
              <div key={j} className="flex items-center gap-4 p-4 rounded-[16px] transition"
                   style={{ border: "1px solid var(--line-soft)", background: "white" }}>
                {p.image_url && <img src={p.image_url} alt={p.product_name} className="w-16 h-16 object-contain rounded-xl shrink-0" loading="lazy" />}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold truncate" style={{ color: "var(--ink-900)" }}>{p.product_name}</h4>
                  <span className="text-xs" style={{ color: "var(--ink-400)" }}>{p.brand}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="num-display text-lg" style={{ color: a.primaryDark }}>{(p.body_price || 0).toLocaleString()}원</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Rental Pricing Section ── */
function RentalPricingSection({ sec, bg, showcaseSelections }) {
  const [ref, visible] = useReveal();
  const columns = sec.custom_columns || [
    { key: "deposit_text", label: "보증금" },
    { key: "output_desc", label: "기본 출력매수" },
    { key: "contract_period", label: "약정기간" },
    { key: "options_desc", label: "선택사항" },
  ];

  /* ── 옵션 선택에 따른 동적 output_desc 계산 ── */
  const computeDynamicOutputDesc = (original) => {
    if (!showcaseSelections || !showcaseSelections.groups || showcaseSelections.groups.length === 0) return original;
    const { opts, groups } = showcaseSelections;
    const lines = [];
    groups.forEach((g, gi) => {
      const oi = opts[gi];
      if (oi == null) return;
      const opt = (g.options || [])[oi];
      if (!opt) return;
      const up = Number(opt.unit_price);
      if (up > 0) {
        if (!isPerUnitPriceGroup(g)) {
          lines.push(`${g.group_name}: ${opt.name} (${up.toLocaleString()}원)`);
        } else {
          const qtyMatch = (opt.name || "").replace(/[,\s]/g, "").match(/(\d+)/);
          const qtyNum = qtyMatch ? Number(qtyMatch[1]) : 0;
          const qtyStr = qtyNum > 0 ? qtyNum.toLocaleString() + "매" : opt.name;
          const totalAmt = getOptionPriceAmount(g, opt);
          lines.push(`${g.group_name} ${qtyStr} (합계 ${totalAmt.toLocaleString()}원)`);
        }
      }
    });
    return lines.length > 0 ? lines.join("\n") : original;
  };

  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-5xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {sec.title && (
          <div className="text-center mb-10">
            <div className="t-eyebrow mb-2.5" style={{ color: "var(--rent-600)" }}>월 렌탈료 비교</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>
          </div>
        )}
        {(sec.products || []).map((p, j) => (
          <div key={j} className="mb-8">
            {/* Plan header — green gradient */}
            <div className="rounded-t-[20px] px-6 py-4 flex items-center justify-between"
                 style={{ background: "linear-gradient(135deg, var(--rent-700) 0%, var(--rent-900) 100%)" }}>
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: "white", color: "var(--rent-700)", boxShadow: "var(--sh-1)" }}>
                  {String(j+1).padStart(2,'0')}
                </span>
                <span className="text-white font-bold text-base md:text-lg">{p.product_name || "제품명"}</span>
              </div>
              <span className="num-display text-white text-xl md:text-2xl">월 {(p.monthly_total || 0).toLocaleString()}원</span>
            </div>
            {/* Plan table */}
            <div className="rounded-b-[20px] overflow-hidden bg-white"
                 style={{ border: "1px solid var(--line-soft)", borderTop: "none", boxShadow: "var(--sh-1)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "var(--rent-50)", borderBottom: "1px solid var(--rent-100)" }}>
                    {columns.map((col, ci) => (
                      <th key={ci} className="px-4 py-3 text-center font-bold" style={{ color: "var(--rent-700)" }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {columns.map((col, ci) => {
                      let val = (p.col_values || {})[col.key] || "-";
                      if (col.key === "output_desc") val = computeDynamicOutputDesc(val);
                      return <td key={ci} className="px-4 py-3.5 text-center whitespace-pre-line" style={{ color: "var(--ink-700)" }}>{val}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            {sec.footer_note && (
              <p className="text-xs mt-2 pl-1" style={{ color: "var(--ink-400)" }}>{sec.footer_note}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Toner/Consumables Section (accent-aware) ── */
function TonerSpecSection({ sec, bg, accent = "sale" }) {
  const [ref, visible] = useReveal();
  const a = accentVars(accent);
  return (
    <section ref={ref} className={`${bg} py-16`}>
      <div className={`max-w-4xl mx-auto px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {sec.title && (
          <div className="mb-6">
            <div className="t-eyebrow mb-2.5" style={{ color: a.primaryDark }}>호환 소모품</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{sec.title}</h2>
          </div>
        )}
        <div className="space-y-3">
          {(sec.products || []).map((p, j) => (
            <div key={j} className="flex items-center gap-4 p-4 rounded-[16px] transition"
                 style={{ border: "1px solid var(--line-soft)", background: "white" }}>
              {p.image_url && <img src={p.image_url} alt={p.product_name} className="w-14 h-14 object-contain rounded-xl shrink-0" loading="lazy" />}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold truncate" style={{ color: "var(--ink-900)" }}>{p.product_name}</h4>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  <span className="tag-pill" style={{ fontSize: 10, padding: "1px 7px", background: "var(--ink-50)", color: "var(--ink-500)" }}>{p.brand}</span>
                  {p.genuine_type && (
                    <span className={`tag-pill ${p.genuine_type === "정품" ? "tag-sale" : "tag-warn"}`} style={{ fontSize: 10, padding: "1px 7px" }}>
                      {p.genuine_type}
                    </span>
                  )}
                  {p.color && <span className="tag-pill" style={{ fontSize: 10, padding: "1px 7px", background: "var(--ink-50)", color: "var(--ink-500)" }}>{p.color}</span>}
                </div>
              </div>
              {!!p.market_price && <div className="num-display text-lg shrink-0" style={{ color: "var(--hot)" }}>{p.market_price.toLocaleString()}원</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ── Compatible Products Section (rent-soft background) ── */
function CompatibleProductsSection({ products, currentCategory }) {
  const isToner = currentCategory === "토너소모품";
  const title = isToner ? "호환 기종" : "호환 토너";
  const subtitle = isToner ? "이 토너와 호환되는 기종입니다" : "이 기종에 사용 가능한 토너입니다";

  return (
    <section className="py-14 md:py-20" style={{ background: "var(--ink-25)" }}>
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-8">
          <div className="t-eyebrow mb-2.5" style={{ color: "var(--rent-600)" }}>관련 제품</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ color: "var(--ink-900)", letterSpacing: "-0.025em" }}>{title}</h2>
          <p className="text-sm" style={{ color: "var(--ink-500)" }}>{subtitle}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {products.map(p => (
            <a key={p.id} href={`/p/${p.slug}`}
              className="bg-white rounded-[16px] p-4 flex flex-col items-center text-center transition-all duration-300 group"
              style={{ border: "1px solid var(--line-soft)" }}>
              <div className="w-full aspect-square flex items-center justify-center mb-3">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.model_name || p.name} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                ) : (
                  <div className="w-16 h-16 rounded-[12px] flex items-center justify-center" style={{ background: "var(--ink-50)" }}>
                    <svg className="w-8 h-8" style={{ color: "var(--ink-300)" }} fill="none" stroke="currentColor" strokeWidth={0.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659" /></svg>
                  </div>
                )}
              </div>
              {p.color_type && (
                <span className={`tag-pill mb-1 ${
                  p.color_type === 'color' ? 'tag-plum' : 'tag-dark'
                }`} style={{ fontSize: 10, padding: "1px 7px" }}>
                  {p.color_type === 'color' ? '칼라' : '흑백'}
                </span>
              )}
              <p className="text-[12px] font-semibold line-clamp-2 leading-snug" style={{ color: "var(--ink-900)" }}>{p.model_name || p.name}</p>
              {p.brand && <p className="text-[10px] mt-0.5" style={{ color: "var(--ink-400)" }}>{p.brand}</p>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ── CTA Section — with dark theme + glow ── */
function CTASection({ sec, isRental = false, canPurchase = false, quantity = 1, onQuantityChange, onInquiry, onBuyNow, ctaLabel = "" }) {
  const [ref, visible] = useReveal();
  const btnText = sec.button_text || ctaLabel || (isRental ? "견적 문의하기" : "지금 구매하기");
  const handleClick = isRental && onInquiry ? onInquiry : (onBuyNow || (() => window.scrollTo({ top: 0, behavior: 'smooth' })));
  return (
    <section
      ref={ref}
      className="py-28 text-white text-center relative overflow-hidden"
      style={{
        background: isRental
          ? "linear-gradient(135deg, var(--rent-700) 0%, var(--rent-900) 100%)"
          : "linear-gradient(135deg, var(--sale-700) 0%, var(--sale-900) 100%)",
      }}
    >
      {/* Animated accent orbs */}
      <div
        className="absolute top-1/2 left-1/4 w-[400px] h-[400px] rounded-full blur-3xl -translate-y-1/2"
        style={{
          background: isRental ? "rgba(31,182,131,0.18)" : "rgba(10,107,255,0.18)",
          animation: "float 6s ease-in-out infinite",
        }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-[300px] h-[300px] rounded-full blur-3xl -translate-y-1/2"
        style={{
          background: "rgba(255,255,255,0.08)",
          animation: "float 6s ease-in-out 2s infinite",
        }}
      />

      <div className="relative max-w-2xl mx-auto px-6">
        <h2
          className={`reveal ${visible ? "visible" : ""}`}
          style={{
            fontSize: "clamp(28px, 4.5vw, 48px)",
            fontWeight: 800,
            letterSpacing: "-0.035em",
            lineHeight: 1.1,
            marginBottom: 20,
          }}
        >
          {sec.title || "시작하세요"}
        </h2>
        {sec.description && (
          <p className={`text-lg leading-relaxed mb-10 reveal ${visible ? "visible" : ""}`}
             style={{ color: "rgba(255,255,255,0.7)", transitionDelay: "0.15s" }}>
            {sec.description}
          </p>
        )}
        {canPurchase && onQuantityChange && (
          <div className={`flex justify-center mb-4 reveal ${visible ? "visible" : ""}`} style={{ transitionDelay: "0.25s" }}>
            <QuantityInput value={quantity} onChange={onQuantityChange} variant="dark" />
          </div>
        )}
        <div className={`flex gap-3 justify-center flex-wrap reveal ${visible ? "visible" : ""}`} style={{ transitionDelay: "0.3s" }}>
          <a
            href="tel:1600-7542"
            className="inline-flex items-center gap-2 rounded-full font-semibold"
            style={{
              background: "white",
              color: isRental ? "var(--rent-900)" : "var(--sale-900)",
              padding: "16px 24px", fontSize: 16,
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>
            1600-7542
          </a>
          <button
            onClick={handleClick}
            className="group inline-flex items-center gap-2.5 rounded-full font-semibold"
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              color: "white",
              padding: "16px 24px", fontSize: 16,
              backdropFilter: "blur(10px)",
            }}
          >
            {btnText}
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════
   상품정보 제공공시 / 배송 / 교환·반품 안내 표
   - 모든 상품: 배송표 + 교환·반품표
   - 토너/잉크/드럼/카트리지: 화학제품 상품정보 제공공시 추가
   ═══════════════════════════════════════════════════ */
function isTonerConsumable(page) {
  const text = [
    page?.title,
    page?.subtitle,
    page?.model_name,
    page?.brand,
    page?.store_category,
    page?.sub_category,
    page?.category,
  ].filter(Boolean).join(" ");
  return /토너|잉크|드럼|카트리지|소모품|toner|ink|drum|cartridge/i.test(text);
}

function ProductPolicyTables({ page }) {
  return (
    <section className="py-16 bg-[#fbfbfd]">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-8 text-center">
          <div className="t-eyebrow mb-2.5 text-[#0071e3]">구매 안내</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1d1d1f]">배송 · 교환 · 반품 · 취소 정책</h2>
        </div>
        <div className="grid gap-6">
          {isTonerConsumable(page) && <TonerDisclosureTable page={page} />}
          <DeliveryTable />
          <ReturnTable />
        </div>
      </div>
    </section>
  );
}

function PolicyRow({ k, v }) {
  return (
    <tr>
      <th
        scope="row"
        className="bg-[#f5f5f7] text-left align-top text-[#6e6e73] font-semibold text-[12px] md:text-[13px] px-4 py-3 w-[120px] md:w-[180px] border-t border-gray-100"
      >
        {k}
      </th>
      <td className="px-4 py-3 text-[#1d1d1f] text-[13px] md:text-[14px] leading-relaxed border-t border-gray-100 align-top">
        {v}
      </td>
    </tr>
  );
}

function PolicyTable({ badge, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-[#1d1d1f]">
          {badge}
        </span>
        <h2 className="text-[17px] md:text-[20px] font-bold text-[#1d1d1f] tracking-tight">{title}</h2>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full border-collapse">
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function TonerDisclosureTable({ page }) {
  const brand = page?.brand || "제조사 표기 참조";
  const model = page?.model_name || page?.title || "-";
  const isGenuine = /정품|genuine/i.test(`${page?.title || ""} ${page?.subtitle || ""}`);
  const isRefill = /재생|리필/.test(`${page?.title || ""} ${page?.subtitle || ""}`);
  const productType = isGenuine ? "정품 토너" : isRefill ? "재생 토너" : "호환/정품 토너";
  return (
    <PolicyTable badge="🧪 상품정보 제공공시" title="화학제품 (토너) 정보 고시">
      <PolicyRow k="품명 및 모델명" v={`${model} (${productType})`} />
      <PolicyRow k="인증·허가 사항" v="전자상거래법상 사무용 토너 — 별도 KC 인증 대상 아님" />
      <PolicyRow k="제조국" v="제조사 표기 참조" />
      <PolicyRow k="제조자 / 수입자" v={brand} />
      <PolicyRow
        k="주요 성분"
        v="토너 분말 — 스티렌-아크릴 공중합체, 카본블랙(흑색) 또는 유기/무기 안료(컬러), 산화철, 이형제(왁스), 대전제어제 등"
      />
      <PolicyRow
        k="사용 시 주의사항"
        v={
          <ul className="list-disc pl-4 space-y-1">
            <li>어린이의 손이 닿지 않는 곳에 보관하세요.</li>
            <li>직사광선·고온다습 환경(40℃ 이상)에 두지 마세요.</li>
            <li>토너 분진 흡입 시 환기, 피부·눈에 닿으면 즉시 흐르는 물로 세척하세요.</li>
            <li>화기 근처에서 보관·폐기하지 마세요. 빈 카트리지는 {BRAND.name} 회수 서비스를 이용하세요.</li>
          </ul>
        }
      />
      <PolicyRow k="품질보증 기준" v="제조사 보증서 또는 소비자분쟁해결기준에 따름" />
      <PolicyRow k="A/S 책임자 / 연락처" v={`${BRAND.name} 고객센터 / 1600-7542 (평일 09:00~18:00)`} />
      <tr>
        <td colSpan={2} className="px-4 py-3 bg-[#fbfbfd] text-[11px] text-[#86868b] border-t border-gray-100">
          ※ 정확한 화학물질 정보(MSDS)는 제조사 공식 자료를 따릅니다. 본 표는 「전자상거래 등에서의 상품 등의 정보제공에 관한 고시」 화학생활용품 항목 기준 안내입니다.
        </td>
      </tr>
    </PolicyTable>
  );
}

function DeliveryTable() {
  return (
    <PolicyTable badge="🚚 배송" title="배송 안내">
      <PolicyRow k="배송 방법" v="택배 / 직배송 (대형 복합기·기업 일괄 납품)" />
      <PolicyRow k="배송 지역" v="전국 (도서산간 일부 지역 제외)" />
      <PolicyRow k="배송 비용" v="무료 · 도서산간 지역은 추가 비용이 발생할 수 있습니다" />
      <PolicyRow
        k="배송 기간"
        v="일반 택배 결제 후 2~3영업일 / 대형 장비 설치 배송은 결제 후 담당자 일정 협의"
      />
      <PolicyRow
        k="출고 안내"
        v="평일 14시 이전 결제 건은 당일 출고를 원칙으로 하며, 그 외 1영업일 이내 출고됩니다."
      />
      <PolicyRow k="배송 문의" v="1600-7542 (평일 09:00~18:00)" />
    </PolicyTable>
  );
}

function ReturnTable() {
  return (
    <PolicyTable badge="↩︎ 교환·반품" title="교환 · 반품 안내">
      <PolicyRow
        k="주문 취소"
        v="상품 출고 전에는 마이페이지 주문 내역 또는 고객센터를 통해 취소할 수 있으며, 출고 이후에는 교환·반품 절차로 접수됩니다."
      />
      <PolicyRow
        k="신청 기간"
        v="상품 수령 후 7일 이내 (미개봉 · 미사용 상품에 한함)"
      />
      <PolicyRow
        k="가능 사유"
        v={
          <ul className="list-disc pl-4 space-y-1">
            <li>표시·광고 내용과 다른 경우</li>
            <li>제품 하자 또는 오배송</li>
            <li>운송 중 파손·훼손</li>
          </ul>
        }
      />
      <PolicyRow
        k="불가 사유"
        v={
          <ul className="list-disc pl-4 space-y-1">
            <li>고객 사유로 포장 또는 상품이 훼손된 경우</li>
            <li>사용 흔적이 있는 토너·드럼·소모품</li>
            <li>주문 제작·기업 맞춤 사양 상품</li>
            <li>수령 후 7일이 경과한 경우</li>
          </ul>
        }
      />
      <PolicyRow
        k="비용 부담"
        v="제품 불량 · 오배송: 무료 / 고객 단순변심: 왕복 배송비 고객 부담"
      />
      <PolicyRow
        k="환불 절차"
        v="반품 상품 회수 확인 후 3영업일 이내 결제 수단으로 환불됩니다."
      />
      <PolicyRow
        k="신청 방법"
        v={
          <>
            마이페이지 → 주문 내역에서 신청 또는 1600-7542 ·{" "}
            <Link to="/policy" className="text-[#0071e3] underline">
              교환·반품 정책 자세히 보기 →
            </Link>
          </>
        }
      />
    </PolicyTable>
  );
}
